import { useEffect, useState, useMemo } from "react";
import {
  Clock,
  LineChart,
  Shield,
  ClipboardList,
  Trash2,
} from "lucide-react";
import PlantTourModal from "../components/PlantTourModal";
import BakeryNoidaModal from "../components/BakeryNoidaModal";
import { loginRequest } from "../auth/authConfig";
import { useMsal } from "@azure/msal-react";
import { fetchEmployeeList } from "../Services/getEmployeeDetails";
import { useDispatch, useSelector } from "react-redux";
import store from "../store/store";
import DashboardLayout from "../components/DashboardLayout";
import { setPlantTourId, setEmployeeDetails, clearAllDataExceptEssential, setSummaryData, setCycleData, setLastFetchTimestamp, setCategorySummary, setCycleCount } from "../store/planTourSlice";
import { setOfflineStarted, setOfflineCompleted, setProgress, resetOfflineState } from "../store/stateSlice.ts";
import { clearUser } from "../store/userSlice";
import { resetCreamPercentage } from "../store/creamPercentageSlice";
import { clearAllData as clearSieveAndMagnetNewPlantData } from "../store/sieveAndMagnetNewPlantSlice";
import { clearAllData as clearSieveAndMagnetOldPlantData } from "../store/sieveAndMagnetOldPlantSlice";
import { clearAllData as clearProductMonitoringData } from "../store/productMonitoringSlice";
import { setFetchedCycles, clearOfflineData } from "../store/CodeVerificationSlice";
import { setFetchedCycles as setSITFetchedCycles, clearOfflineData as clearSealIntegrityOfflineData } from "../store/SealIntegrityTestSlice";
import { clearOfflineData as clearALCOfflineData, setFetchedCycles as setALCFetchedCycles } from "../store/ALCSlice";
import { setFetchedCycles as setNWMFetchedCycles, clearOfflineData as clearNWMOfflineData } from "../store/NetWeightMonitoringRecordSlice";
import { clearOfflineData as clearBakingOfflineData } from "../store/BakingProcessSlice";
import { clearOfflineData as clearOPRPAndCcpOfflineData } from "../store/OPRPAndCCPSlice";
import { clearOfflineData as clearBakingAndCoolingOfflineData, setFetchedCycles as setBakingAndCoolingFetchedCycles, addOfflineFile as addBakingAndCoolingOfflineFile } from "../store/BakingAndCoolingSlice";
import { setFetchedCycles as setBackingCodingFetchedCycles, addOfflineFile as addBackingCodingOfflineFile, resetState as resetBackingCodingState } from "../store/BackingCodingSlice";
import { setFetchedCycles as setBackingMixingFetchedCycles, resetState as resetBackingMixingState } from "../store/BackingMixingSlice";
import { setFetchedCycles as setProcessFormatFetchedCycles, resetState as resetProcessFormatState } from "../store/ProcessFormatSlice";
import { setFetchedCycles as setProofingFetchedCycles, resetState as resetProofingState } from "../store/ProofingSlice";
import { setFetchedCycles as setBakingSievesAndMagnetsFetchedCycles, resetState as resetBakingSievesAndMagnetsState } from "../store/BakingSievesAndMagnetsSlice";
import { resetState as resetBackingPQIState } from "../store/backingPQISlice";
import { hasOfflineDataToSync, clearAllOfflineData } from "../Services/PlantTourSyncService";
import { getAllOfflineData } from "../Services/PlantTourOfflineStorage";
import { syncOfflineFiles, getMsalToken } from "../Services/BakingProcessFileUpload";
import { setOfflineCriteriaList, setOfflineEmployeeDetails, setOfflineExistingObservations, setOfflineDataTimestamp, setOfflineMode } from "../store/planTourSlice";
import { setPlantTourId as setPlantTourSectionPlantTourId } from "../store/plantTourSectionSlice";
import { createOrFetchDepartmentTour } from "../Services/createOrFetchDepartmentTour";
import * as CriteriaMasterService from "../Services/CriteriaMasterService";
import * as PlantTourService from "../Services/PlantTourService";
import { savesectionApicall as saveBakingSection, collectEstimationDataCycleSave as collectBakingForSync } from "../Services/BakingProcesRecord";
import { saveSectionApiCall as saveSealSection } from "../Services/SealIntegrityTest.ts";
import type { CodeVerificationCycleData } from "../Services/CodeVerificationRecord";
import { createOrFetchPlantTour } from "../Services/createOrFetchPlantTour";
import { getAccessToken } from "../Services/getAccessToken";
import { saveSectionData } from "../Services/saveSectionData";
import { saveCreamPercentageData } from "../Services/saveCreamPercentageData";
import { fetchSummaryData } from "../Services/getSummaryData";
import { fetchCycleDetails } from "../Services/getCycleDetails";
import { useNavigate } from "react-router-dom";

// Process data by category and count defects (copied from ProductQualityIndex)
function processData(data: any[]) {
  const summary: Record<string, { okays: number; aDefects: number; bDefects: number; cDefects: number }> = {};
  const uniqueCycles = new Set();

  data.forEach(item => {
    const category = item.cr3ea_category || 'Unknown';
    const cycle = item.cr3ea_cycle;

    if (cycle) {
      uniqueCycles.add(cycle);
    }

    if (!summary[category]) {
      summary[category] = { okays: 0, aDefects: 0, bDefects: 0, cDefects: 0 };
    }

    if (item.cr3ea_criteria === 'Okay') {
      summary[category].okays++;
    } else {
      if (item.cr3ea_defectcategory === 'Category A') summary[category].aDefects++;
      if (item.cr3ea_defectcategory === 'Category B') summary[category].bDefects++;
      if (item.cr3ea_defectcategory === 'Category C') summary[category].cDefects++;
    }
  });

  // Return both summary and cycle count
  return {
    summary,
    totalCycles: uniqueCycles.size
  };
}

// Remove duplicate submissions - include product+batch so multiple products per cycle are each synced
function removeDuplicateSubmissions(submissions: any[]) {
  const uniqueSubmissions: any[] = [];
  const seenSubmissions = new Set<string>();

  for (const submission of submissions) {
    const category = submission.records?.[0]?.cr3ea_category || 'Unknown';
    const productName = submission.records?.[0]?.cr3ea_productname || '';
    const batchNo = submission.records?.[0]?.cr3ea_batchno || '';
    const submissionKey = `${category}-${submission.cycleNo}-${productName}-${batchNo}-${submission.timestamp || 0}`;

    if (!seenSubmissions.has(submissionKey)) {
      seenSubmissions.add(submissionKey);
      uniqueSubmissions.push(submission);
    }
  }

  return uniqueSubmissions;
}


export default function HomePage() {
  console.log('HomePage component rendering...');
  const { accounts, instance } = useMsal();
  const [employees, setEmployees] = useState<any[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBakeryNoidaModalOpen, setIsBakeryNoidaModalOpen] = useState(false);
  const [isOfflineLoading, setIsOfflineLoading] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showOfflineError, setShowOfflineError] = useState(false);
  const dispatch = useDispatch();

  // Get offline state from Redux
  const isOfflineStarted = useSelector((state: any) => state.appState.isOfflineStarted);
  const isOfflineCompleted = useSelector((state: any) => state.appState.isOfflineCompleted);
  const progress = useSelector((state: any) => state.appState.progress);
  const offlineSubmissions = useSelector((state: any) => state.appState.offlineSubmissions);
  const offlineSubmissionsByCategory = useSelector((state: any) => state.appState.offlineSubmissionsByCategory);
  // Flattened count for PQI (CBB/Secondary/Primary/Product) stored by category
  const pqiOfflineCount = Object.values(offlineSubmissionsByCategory || {}).reduce((sum: number, arr: any) => sum + (Array.isArray(arr) ? arr.length : 0), 0);

  // Get cream percentage offline data from Redux
  const creamPercentagePendingSync = useSelector((state: any) => state.creamPercentage.pendingSync);
  
  // Get Sieve and Magnet New Plant offline data from Redux
  const sieveAndMagnetNewPlantPendingSync = useSelector((state: any) => state.sieveAndMagnetNewPlant.pendingSync);
  
  // Get Sieve and Magnet Old Plant offline data from Redux
  const sieveAndMagnetOldPlantPendingSync = useSelector((state: any) => state.sieveAndMagnetOldPlant.pendingSync);
  
  // Get Product Monitoring offline data from Redux
  const productMonitoringPendingSync = useSelector((state: any) => state.productMonitoring.pendingSync);
  
  // Get Code Verification offline data from Redux
  const codeVerificationOfflineData = useSelector((state: any) => state.codeVerification.offlineSavedData);
  // Get Baking Process offline data from Redux
  const bakingOfflineData = useSelector((state: any) => state.bakingProcess?.offlineSavedData || []);
  // Get Seal Integrity Test offline data from Redux
  const sealIntegrityOfflineData = useSelector((state: any) => state.sealIntegrityTest?.offlineSavedData || []);
  // Get ALC offline data from Redux
  const alcOfflineData = useSelector((state: any) => state.ALC?.offlineSavedData || []);
  // Get Net Weight Monitoring offline data from Redux
  const netWeightOfflineData = useSelector((state: any) => state.NetWeightMonitoring?.offlineSavedData || []);
  // Get OPRP and CCP offline data from Redux
  const oprpAndCcpOfflineData = useSelector((state: any) => state.oprpAndCcp?.offlineSavedData || []);
  // Get Baking and Cooling offline data from Redux
  const bakingAndCoolingOfflineData = useSelector((state: any) => state.bakingAndCooling?.offlineSavedData || []);
  const bakingAndCoolingOfflineFilesCount = useSelector((state: any) => {
    const files = state.bakingAndCooling?.offlineFiles || [];
    // Only count files with fileData (uploaded while offline), not fetched files
    return files.filter((f: any) => f.fileData && f.fileData.trim() !== '').length;
  });
  const backingCodingOfflineData = useSelector((state: any) => state.backingCoding?.offlineSavedData || []);
  const backingMixingOfflineData = useSelector((state: any) => state.backingMixing?.offlineSavedData || []);
  const processFormatOfflineData = useSelector((state: any) => state.processFormat?.offlineSavedData || []);
  const proofingOfflineData = useSelector((state: any) => state.proofing?.offlineSavedData || []);
  const bakingSievesAndMagnetsOfflineData = useSelector((state: any) => state.bakingSievesAndMagnets?.offlineSavedData || []);
  const backingPQIOfflineQueueCount = useSelector((state: any) => (state.backingPQI?.offlineQueue || []).length);
  const backingCodingOfflineFilesCount = useSelector((state: any) => {
    const files = state.backingCoding?.offlineFiles || [];
    // Only count files with fileData (uploaded while offline), not fetched files
    return files.filter((f: any) => f.fileData && f.fileData.trim() !== '').length;
  });

  const user = useSelector((state: any) => state.user.user);
  const userState = useSelector((state: any) => state.user);
  const planTourState = useSelector((state: any) => state.planTour);
  const employeeDetails = useSelector((state: any) => state.planTour.employeeDetails);
  const [isViewAllOpen, setIsViewAllOpen] = useState(false);
  const [isPlantTourLoading, setIsPlantTourLoading] = useState(false);
  const navigate = useNavigate();

  // Helper function to get department name with fallback
  const getDepartmentName = () => {
    // First try to get from local employees state (when online)
    if (employees && employees.length > 0 && employees[0]?.departmentName) {
      console.log('Using department from local employees state:', employees[0].departmentName);
      return employees[0].departmentName;
    }
    // Fallback to Redux employee details (persisted data)
    if (employeeDetails?.departmentName) {
      console.log('Using department from Redux employee details:', employeeDetails.departmentName);
      return employeeDetails.departmentName;
    }
    // Default fallback
    console.log('Using default department fallback: Quality - Rajpura');
    return "Quality - Rajpura";
  };

  // Calculate total offline count with better reliability
  const totalOfflineCount = useMemo(() => {
    // Get Plant Tour Section offline data count
    let plantTourSectionOfflineCount = 0;
    try {
      const allPlantTourData = getAllOfflineData();
      plantTourSectionOfflineCount = Object.values(allPlantTourData).reduce((total: number, tourData: any) => {
        return total + (tourData.observations?.length || 0);
      }, 0);
    } catch (error) {
      console.error('Error getting Plant Tour Section offline count:', error);
    }

    // Get pause and finish data count
    let pauseDataCount = 0;
    let finishDataCount = 0;
    try {
      const offlineData = JSON.parse(localStorage.getItem('plantTourOfflineData') || '{}');
      if (offlineData.pauseData) {
        pauseDataCount = 1; // Count pause data as 1 item
      }
      if (offlineData.finishData) {
        finishDataCount = 1; // Count finish data as 1 item
      }
    } catch (error) {
      console.error('Error getting pause/finish data count:', error);
    }

    const count = pqiOfflineCount + 
      creamPercentagePendingSync.length + 
      sieveAndMagnetNewPlantPendingSync.length + 
      sieveAndMagnetOldPlantPendingSync.length + 
      productMonitoringPendingSync.length + 
      codeVerificationOfflineData.length + 
      bakingOfflineData.length + 
      sealIntegrityOfflineData.length + 
      alcOfflineData.length + 
      netWeightOfflineData.length + 
      oprpAndCcpOfflineData.length +
      bakingAndCoolingOfflineData.length +
      (bakingAndCoolingOfflineFilesCount || 0) +
      backingCodingOfflineData.length +
      (backingCodingOfflineFilesCount || 0) +
      backingMixingOfflineData.length +
      processFormatOfflineData.length +
      proofingOfflineData.length +
      bakingSievesAndMagnetsOfflineData.length +
      backingPQIOfflineQueueCount +
      plantTourSectionOfflineCount +
      pauseDataCount +
      finishDataCount;
    
    console.log('HomePage: Calculating total offline count:', count);
    console.log('HomePage: Breakdown:', {
      pqiOfflineCount,
      backingPQIOfflineQueueCount,
      creamPercentagePendingSync: creamPercentagePendingSync.length,
      sieveAndMagnetNewPlantPendingSync: sieveAndMagnetNewPlantPendingSync.length,
      sieveAndMagnetOldPlantPendingSync: sieveAndMagnetOldPlantPendingSync.length,
      productMonitoringPendingSync: productMonitoringPendingSync.length,
      codeVerificationOfflineData: codeVerificationOfflineData.length,
      bakingOfflineData: bakingOfflineData.length,
      sealIntegrityOfflineData: sealIntegrityOfflineData.length,
      alcOfflineData: alcOfflineData.length,
      netWeightOfflineData: netWeightOfflineData.length,
      oprpAndCcpOfflineData: oprpAndCcpOfflineData.length,
      bakingAndCoolingOfflineData: bakingAndCoolingOfflineData.length,
      bakingAndCoolingOfflineFilesCount: bakingAndCoolingOfflineFilesCount || 0,
      backingCodingOfflineData: backingCodingOfflineData.length,
      backingCodingOfflineFilesCount: backingCodingOfflineFilesCount || 0,
      backingMixingOfflineData: backingMixingOfflineData.length,
      processFormatOfflineData: processFormatOfflineData.length,
      proofingOfflineData: proofingOfflineData.length,
      bakingSievesAndMagnetsOfflineData: bakingSievesAndMagnetsOfflineData.length,
      plantTourSectionOfflineCount,
      pauseDataCount,
      finishDataCount
    });
    
    return count;
  }, [pqiOfflineCount, backingPQIOfflineQueueCount, creamPercentagePendingSync.length, sieveAndMagnetNewPlantPendingSync.length, sieveAndMagnetOldPlantPendingSync.length, productMonitoringPendingSync.length, codeVerificationOfflineData.length, bakingOfflineData.length, sealIntegrityOfflineData.length, alcOfflineData.length, netWeightOfflineData.length, oprpAndCcpOfflineData.length, bakingAndCoolingOfflineData.length, bakingAndCoolingOfflineFilesCount, backingCodingOfflineData.length, backingCodingOfflineFilesCount, backingMixingOfflineData.length, processFormatOfflineData.length, proofingOfflineData.length, bakingSievesAndMagnetsOfflineData.length]);

  const metrics = [
    { label: "5S", icon: <Clock className="text-orange-500" />, count: 0 },
    { label: "Performance", icon: <LineChart className="text-orange-500" />, count: 0 },
    { label: "Safety", icon: <Shield className="text-orange-500" />, count: 0 },
    { label: "Quality", icon: <ClipboardList className="text-orange-500" />, count: 0 },
    { label: "Wastage", icon: <Trash2 className="text-orange-500" />, count: 0 },
  ];


  const handleOfflineTour = async () => {
    // Clear only pending/offline SieveandMagnet data, not completed cycles
    console.log('Clearing pending SieveandMagnet data before starting offline tour...');
    dispatch(clearSieveAndMagnetNewPlantData());
    dispatch(clearSieveAndMagnetOldPlantData());
    
    // CRITICAL: Check if already in offline mode - if so, don't call ANY APIs
    const currentOfflineState = store.getState().appState?.isOfflineStarted || false;
    if (currentOfflineState) {
      console.warn('Already in offline mode - skipping ALL API calls');
      alert('System is already in offline mode. No API calls will be made.');
      return;
    }
    
    // CRITICAL: Check network status BEFORE making any API calls
    const networkStatus = navigator.onLine;
    console.log('=== STARTING OFFLINE MODE SETUP ===');
    console.log('Network status check:', networkStatus ? 'ONLINE - will fetch APIs' : 'OFFLINE - using fallbacks only');
    
    setIsOfflineLoading(true);
    dispatch(setProgress(0));
    dispatch(setOfflineCompleted(false));
    
    // CRITICAL: If offline, skip ALL API calls, use fallbacks, and set offline mode immediately
    if (!networkStatus) {
      console.warn('Network is offline - skipping ALL API calls and using fallback values');
      setShowOfflineError(true);
      setTimeout(() => setShowOfflineError(false), 5000);
      
      // Use fallback values immediately without any API calls
      if (!user?.Name || user.Name.trim() === '') {
        alert('User name is missing. Cannot start offline mode.');
        setIsOfflineLoading(false);
        return;
      }
      
      // Set fallback employee details
      const fallbackEmployeeDetails = {
        id: null,
        Title: user.Name,
        employeeName: user.Name.trim(),
        departmentId: '135',
        departmentName: 'N/A',
        roleName: 'QA',
        roleId: '1',
        plantId: '1',
        plantName: 'N/A',
      };
      dispatch(setEmployeeDetails(fallbackEmployeeDetails));
      
      // Generate temporary plant tour ID
      const tempPlantTourId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      dispatch(setPlantTourId(tempPlantTourId));
      
      // Initialize empty data arrays
      dispatch(setSummaryData([]));
      dispatch(setCycleData([]));
      dispatch(setCategorySummary({}));
      dispatch(setCycleCount(0));
      
      // Mark as offline mode immediately - NO API calls will happen after this
      dispatch(setOfflineCompleted(true));
      dispatch(setOfflineStarted(true));
      dispatch(setProgress(100));
      
      console.log('Offline mode activated with fallback values - NO API calls made');
      setIsOfflineLoading(false);
      return; // Exit early - no API calls
    }

    try {
      console.log('Starting offline mode setup...');
      console.log('Network status:', networkStatus ? 'ONLINE - will attempt API calls' : 'OFFLINE - using fallbacks only');

      // Step 1: Get access token first - ONLY if online
      let tokenResult: { token: string; expiry: number } | null = null;
      if (!networkStatus) {
        // CRITICAL: If offline, skip ALL API calls including getAccessToken
        console.log('Network is offline - skipping getAccessToken API call completely');
        tokenResult = { token: 'offline-temp-token', expiry: Date.now() / 1000 + 3600 };
      } else {
        // Only call getAccessToken if online
        try {
          tokenResult = await getAccessToken();
          if (tokenResult && tokenResult.token) {
            console.log('Token generated successfully', tokenResult);
          } else {
            console.warn('Failed to get access token, will use fallback for offline mode');
            tokenResult = { token: 'offline-temp-token', expiry: Date.now() / 1000 + 3600 };
          }
        } catch (tokenError) {
          console.error('Error getting access token:', tokenError);
          console.warn('Using fallback token for offline mode');
          tokenResult = { token: 'offline-temp-token', expiry: Date.now() / 1000 + 3600 };
        }
      }

      dispatch(setProgress(20));

      // Step 2: Fetch employee list - ONLY if online
      console.log('Fetching employee list...');
      console.log('User name being searched:', user?.Name);
      
      if (!user?.Name || user.Name.trim() === '') {
        throw new Error('User name is missing or empty. Please ensure you are logged in properly.');
      }
      
      let employeeDetails: any;
      if (!networkStatus) {
        // CRITICAL: If offline, skip ALL API calls including acquireTokenSilent and fetchEmployeeList
        console.log('Network is offline - skipping acquireTokenSilent and fetchEmployeeList API calls completely');
        // Use fallback immediately without any API calls
        employeeDetails = {
          id: null,
          Title: user.Name,
          employeeName: user.Name.trim(),
          departmentId: '135',
          departmentName: 'N/A',
          roleName: 'QA',
          roleId: '1',
          plantId: '1',
          plantName: 'N/A',
        };
        dispatch(setEmployeeDetails(employeeDetails));
        console.log('Using fallback employee details (offline mode - no API calls):', employeeDetails);
      } else {
        // Only call APIs if online
        try {
          const response = await instance.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0],
          });
          
          const employeeList = await fetchEmployeeList(response.accessToken, user.Name.trim());
          console.log('Employee list API response:', employeeList);
          console.log('Number of employees found:', employeeList?.length || 0);
          
          if (employeeList && employeeList.length > 0) {
            employeeDetails = employeeList[0];
            dispatch(setEmployeeDetails(employeeDetails));
            console.log('Employee details stored in Redux:', employeeDetails);
          } else {
            throw new Error('Employee list empty');
          }
        } catch (employeeError) {
          console.error('Error fetching employee list:', employeeError);
          // Fallback: use logged-in user name with default values
          console.warn('Using fallback employee details for offline plant tour.');
          employeeDetails = {
            id: null,
            Title: user.Name,
            employeeName: user.Name.trim(),
            departmentId: '135',
            departmentName: 'N/A',
            roleName: 'QA',
            roleId: '1',
            plantId: '1',
            plantName: 'N/A',
          };
          dispatch(setEmployeeDetails(employeeDetails));
          console.log('Fallback employee details stored in Redux:', employeeDetails);
        }
      }

      dispatch(setProgress(40));

      // Step 3: Create or fetch Plant Tour ID - ONLY if online
      console.log('Creating/fetching Plant Tour ID...');
      let plantTourId: string | null = null;
      if (networkStatus && tokenResult && tokenResult.token && tokenResult.token !== 'offline-temp-token') {
        try {
          plantTourId = await createOrFetchPlantTour({
            accessToken: tokenResult.token,
            departmentId: employeeDetails.departmentId || '135',
            employeeName: employeeDetails.employeeName || user?.Name || '',
            roleName: employeeDetails.roleName || 'QA',
            plantId: employeeDetails.plantId || '1',
            userRoleID: employeeDetails.roleId || '1',
          });
          
          if (plantTourId) {
            dispatch(setPlantTourId(plantTourId));
            console.log('Plant Tour ID created and stored in Redux:', plantTourId);
          } else {
            throw new Error('Failed to create/fetch Plant Tour ID');
          }
        } catch (plantTourError) {
          console.error('Error creating/fetching Plant Tour ID:', plantTourError);
          // Generate temporary ID
          const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
          plantTourId = tempId;
          dispatch(setPlantTourId(plantTourId));
          console.warn('Using temporary Plant Tour ID for offline mode:', plantTourId);
        }
      } else {
        console.log('Skipping createOrFetchPlantTour - network is offline or no valid token');
        // Generate temporary ID immediately without API call
        const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        plantTourId = tempId;
        dispatch(setPlantTourId(plantTourId));
        console.log('Using temporary Plant Tour ID (offline mode):', plantTourId);
      }

      dispatch(setProgress(60));

      // Step 3.5: Fetch existing SieveandMagnetnewplant completed cycles - ONLY if online
      console.log('Fetching existing SieveandMagnetnewplant completed cycles...');
      try {
        if (!networkStatus || !plantTourId || plantTourId.startsWith('temp-')) {
          console.warn('Skipping SieveandMagnetnewplant fetch - network offline or using temporary plant tour ID');
        } else {
          const { fetchCycleData } = await import('../Services/saveSieveAndMagnetNewPlant');
          const existingSieveAndMagnetCycles = await fetchCycleData(plantTourId);
          console.log('Fetched existing SieveandMagnetnewplant cycles:', existingSieveAndMagnetCycles);
        
        if (existingSieveAndMagnetCycles && existingSieveAndMagnetCycles.length > 0) {
          // Convert fetched data to ReduxCompletedCycleData format
          const completedCyclesData = existingSieveAndMagnetCycles.map((cycle: any) => ({
            cycleNumber: cycle.cycleNum,
            defects: cycle.defects || [],
            okays: cycle.okays || [],
            formData: { product: '', machineNo: '', line: '', standardPercentage: '' }, // Default form data
            remarks: ''
          }));
          
          // Import and dispatch the action to set completed cycles
          const { setCompletedCycles, setCurrentCycle, setLastFetchTimestamp } = await import('../store/sieveAndMagnetNewPlantSlice');
          
          dispatch(setCompletedCycles(completedCyclesData));
          dispatch(setLastFetchTimestamp(Date.now()));
          
          // Set current cycle to the next cycle number
          const maxCycle = Math.max(...existingSieveAndMagnetCycles.map((cycle: any) => cycle.cycleNum));
          dispatch(setCurrentCycle(maxCycle + 1));
          
          console.log('Successfully loaded existing SieveandMagnetnewplant cycles. Max cycle:', maxCycle, 'Next cycle:', maxCycle + 1);
          } else {
            console.log('No existing SieveandMagnetnewplant cycles found on server.');
          }
        }
      } catch (sieveError) {
        console.error('Error loading existing SieveandMagnetnewplant cycle data:', sieveError);
        // Continue with offline mode even if SieveandMagnetnewplant fetch fails
      }

      // Step 3.6: Fetch existing Product Monitoring completed cycles - ONLY if online
      console.log('Fetching existing Product Monitoring completed cycles...');
      try {
        if (!networkStatus || !plantTourId || plantTourId.startsWith('temp-')) {
          console.warn('Skipping Product Monitoring fetch - network offline or using temporary plant tour ID');
        } else {
          const { fetchAndStoreCycleData } = await import('../Services/productMonitoringRecord');
          await dispatch(fetchAndStoreCycleData(plantTourId) as any);
          console.log('Successfully loaded existing Product Monitoring cycles.');
        }
      } catch (productMonitoringError) {
        console.error('Error loading existing Product Monitoring cycle data:', productMonitoringError);
        // Continue with offline mode even if Product Monitoring fetch fails
      }

      // Step 3.7: Fetch existing Code Verification completed cycles - ONLY if online
      console.log('Fetching existing Code Verification completed cycles...');
      try {
        if (!networkStatus || !plantTourId || plantTourId.startsWith('temp-')) {
          console.warn('Skipping Code Verification fetch - network offline or using temporary plant tour ID');
          dispatch(setFetchedCycles([]));
        } else {
          const { fetchCycleData } = await import('../Services/CodeVerificationRecord');
          const existingCodeVerificationCycles = await fetchCycleData(plantTourId);
          console.log('Fetched existing Code Verification cycles:', existingCodeVerificationCycles);
        
        if (existingCodeVerificationCycles && existingCodeVerificationCycles.length > 0) {
          // Store the fetched cycles in Redux
          console.log('Storing Code Verification cycles in Redux:', existingCodeVerificationCycles);
          dispatch(setFetchedCycles(existingCodeVerificationCycles as CodeVerificationCycleData[]));
          console.log('Successfully loaded existing Code Verification cycles:', existingCodeVerificationCycles.length);
          } else {
            console.log('No existing Code Verification cycles found on server.');
            dispatch(setFetchedCycles([]));
          }
        }
      } catch (codeVerificationError) {
        console.error('Error loading existing Code Verification cycle data:', codeVerificationError);
        // Continue with offline mode even if Code Verification fetch fails
        dispatch(setFetchedCycles([]));
      }

      // Step 3.8: Fetch existing Seal Integrity Test completed cycles - ONLY if online
      console.log('Fetching existing Seal Integrity Test completed cycles...');
      try {
        if (!networkStatus || !plantTourId || plantTourId.startsWith('temp-')) {
          console.warn('Skipping Seal Integrity Test fetch - network offline or using temporary plant tour ID');
          dispatch(setSITFetchedCycles([] as any));
        } else {
          const { fetchCycleData } = await import('../Services/SealIntegrityTest.ts');
          const existingSealIntegrityCycles = await fetchCycleData(plantTourId);
          console.log('Fetched existing Seal Integrity Test cycles:', existingSealIntegrityCycles);
          if (existingSealIntegrityCycles && existingSealIntegrityCycles.length > 0) {
            dispatch(setSITFetchedCycles(existingSealIntegrityCycles as any));
            console.log('Successfully loaded existing Seal Integrity Test cycles:', existingSealIntegrityCycles.length);
          } else {
            console.log('No existing Seal Integrity Test cycles found on server.');
            dispatch(setSITFetchedCycles([] as any));
          }
        }
      } catch (sitError) {
        console.error('Error loading existing Seal Integrity Test cycle data:', sitError);
        dispatch(setSITFetchedCycles([] as any));
      }

      // Step 3.9: Fetch existing ALC completed cycles - ONLY if online
      console.log('Fetching existing ALC completed cycles...');
      try {
        if (!networkStatus || !plantTourId || plantTourId.startsWith('temp-')) {
          console.warn('Skipping ALC fetch - network offline or using temporary plant tour ID');
          dispatch(setALCFetchedCycles([] as any));
        } else {
          const { fetchCycleData } = await import('../Services/ALC');
          const existingALCCycles = await fetchCycleData(plantTourId);
          console.log('Fetched existing ALC cycles:', existingALCCycles);
          dispatch(setALCFetchedCycles((existingALCCycles as any) || []));
        }
      } catch (alcError) {
        console.error('Error loading existing ALC cycle data:', alcError);
        dispatch(setALCFetchedCycles([] as any));
      }

      // Step 3.10: Fetch existing Cream Percentage completed cycles - ONLY if online
      console.log('=== FETCHING CREAM PERCENTAGE DATA FROM HOMEPAGE ===');
      console.log('Plant Tour ID for cream percentage:', plantTourId);
      console.log('User name:', user?.Name);
      try {
        if (!networkStatus || !plantTourId || plantTourId.startsWith('temp-')) {
          console.warn('Skipping Cream Percentage fetch - network offline or using temporary plant tour ID');
        } else {
          const { getCreamPercentageData } = await import('../Services/getCreamPercentageData');
          console.log('About to call getCreamPercentageData with qualityTourId:', plantTourId);
          
          // Test the API call
          console.log('Testing API call...');
          const existingCreamPercentageCycles = await getCreamPercentageData({ qualityTourId: plantTourId });
        console.log('API call completed successfully');
        console.log('Successfully fetched existing Cream Percentage cycles:', existingCreamPercentageCycles);
        console.log('Number of cycles fetched:', existingCreamPercentageCycles?.length || 0);
        console.log('Type of response:', typeof existingCreamPercentageCycles);
        console.log('Is array:', Array.isArray(existingCreamPercentageCycles));
        
        if (existingCreamPercentageCycles && existingCreamPercentageCycles.length > 0) {
          // Process fetched data: API returns one entry per product; group by cycle so cycleData[cycleNum] is an array
          const processedCycleData: { [key: number]: any[] } = {};
          
          existingCreamPercentageCycles.forEach((cycle: any) => {
            const cycleNum = parseInt(cycle.cycleNum);
            const productEntry = {
              formData: {
                product: cycle.product,
                machineNo: cycle.machineNo,
                line: cycle.lineNo,
                standardCreamPercentage: cycle.standardCreamPercentage,
                executiveName: ''
              },
              weightData: {
                sandwichWeights: cycle.wtSandwich || ['', '', '', ''],
                shellWeights: cycle.wtShell || ['', '', '', '']
              },
              qualityTourId: plantTourId,
              userName: user?.Name || null,
              shiftValue: 'shift 1',
              timestamp: new Date().toISOString()
            };
            if (!processedCycleData[cycleNum]) {
              processedCycleData[cycleNum] = [];
            }
            processedCycleData[cycleNum].push(productEntry);
            console.log(`Processed Cream Percentage cycle ${cycleNum} product:`, cycle.product, 'total in cycle:', processedCycleData[cycleNum].length);
          });
          
          const processedCycles = [...new Set(Object.keys(processedCycleData).map(Number))].sort((a, b) => a - b);
          
          // Load data into Redux using the cream percentage slice
          console.log('About to load data into Redux...');
          console.log('Processed cycles to store:', processedCycles);
          console.log('Processed cycle data to store:', processedCycleData);
          
          const { loadOfflineData } = await import('../store/creamPercentageSlice');
          const nextCycle = Math.max(...processedCycles, 0) + 1;
          console.log('Next cycle number:', nextCycle);
          
          // Create the payload
          const payload = {
            cycleData: processedCycleData,
            completedCycles: processedCycles,
            currentCycle: nextCycle
          };
          
          console.log('Payload to dispatch:', payload);
          console.log('About to dispatch loadOfflineData...');
          
          dispatch(loadOfflineData(payload));
          
          console.log('loadOfflineData dispatched successfully');
          
          // Wait a bit for Redux state to update
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Check Redux state after dispatch
          const currentState = store.getState();
          const creamPercentageState = currentState.creamPercentage;
          console.log('Redux state after dispatch:', creamPercentageState);
          console.log('Cycle data in Redux:', Object.keys(creamPercentageState.cycleData || {}));
          console.log('Completed cycles in Redux:', creamPercentageState.completedCycles);
          console.log('Current cycle in Redux:', creamPercentageState.currentCycle);
          
          console.log('Successfully loaded existing Cream Percentage cycles:', processedCycles.length);
          console.log('Final processed cycles:', processedCycles);
          console.log('Final processed cycle data:', processedCycleData);
          } else {
            console.log('No existing Cream Percentage cycles found on server.');
          }
        }
      } catch (creamPercentageError) {
        console.error('=== ERROR LOADING CREAM PERCENTAGE DATA ===');
        console.error('Error details:', creamPercentageError);
        console.error('Error message:', creamPercentageError instanceof Error ? creamPercentageError.message : 'Unknown error');
        console.error('Error stack:', creamPercentageError instanceof Error ? creamPercentageError.stack : 'No stack trace');
        // Continue with offline mode even if Cream Percentage fetch fails
      }

      // Step 3.11: Fetch existing Net Weight Monitoring completed cycles - ONLY if online
      console.log('Fetching existing Net Weight Monitoring completed cycles...');
      try {
        if (!networkStatus || !plantTourId || plantTourId.startsWith('temp-')) {
          console.warn('Skipping Net Weight Monitoring fetch - network offline or using temporary plant tour ID');
          dispatch(setNWMFetchedCycles([] as any));
        } else {
          const { fetchCycleData } = await import('../Services/NetWeightMonitoringRecord.ts');
          const existingNWMCycles = await fetchCycleData(plantTourId);
          console.log('Fetched existing Net Weight Monitoring cycles:', existingNWMCycles);
          dispatch(setNWMFetchedCycles((existingNWMCycles as any) || []));
        }
      } catch (nwmError) {
        console.error('Error loading existing Net Weight Monitoring cycle data:', nwmError);
        dispatch(setNWMFetchedCycles([] as any));
      }

      // Step 3.12: Fetch existing Baking and Cooling completed cycles (same pattern as Code Verification / ALC)
      console.log('Fetching existing Baking and Cooling completed cycles...');
      try {
        if (!networkStatus || !plantTourId || plantTourId.startsWith('temp-')) {
          console.warn('Skipping Baking and Cooling fetch - network offline or using temporary plant tour ID');
          dispatch(setBakingAndCoolingFetchedCycles([]));
        } else {
          const { fetchCycleData } = await import('../Services/BakingAndCoolingRecord');
          // Pass token so service does not call getAccessToken (same session, avoids any race)
          const bakingCoolingToken = tokenResult?.token && tokenResult.token !== 'offline-temp-token' ? tokenResult.token : undefined;
          const existingBakingAndCoolingCycles = await fetchCycleData(plantTourId, bakingCoolingToken);
          console.log('Fetched existing Baking and Cooling cycles:', existingBakingAndCoolingCycles);

          if (existingBakingAndCoolingCycles && existingBakingAndCoolingCycles.length > 0) {
            const bakingAndCoolingCycleData = existingBakingAndCoolingCycles.map((cycle: any) => {
              let cycleNumStr = String(cycle.cycleNum || '').replace('Cycle-', '').trim();
              if (!cycleNumStr || cycleNumStr === '') cycleNumStr = '1';
              return {
                cycleNum: cycleNumStr,
                product: cycle.product || '',
                executiveName: cycle.executiveName || '',
                bakingTime: cycle.bakingTime || '',
                lineNumber: cycle.lineNumber || '',
                bakingTempZone1: cycle.bakingtempzone1 || '',
                bakingTempZone2: cycle.bakingtempzone2 || '',
                bakingTempZone3: cycle.bakingtempzone3 || '',
                bakingTempZone4: cycle.bakingtempzone4 || '',
                productTempAfterBaking: cycle.producttempafterbaking || '',
                ap: cycle.pccf_ap || '',
                std: cycle.pccf_std || '',
                ad: cycle.pccf_ad || '',
                ovenFireStartTime: cycle.ovenfirestarttime || '',
                ovenStopTime: cycle.ovenstoptime || '',
                productInTime: cycle.productintime || '',
                productOutTime: cycle.productouttime || '',
                coolingTime: cycle.coolingtime || '',
                coolingTemp: cycle.coolingtemp || '',
                coreTemp: cycle.coretemp || '',
                productColor: cycle.productcolor || ''
              };
            });
            dispatch(setBakingAndCoolingFetchedCycles(bakingAndCoolingCycleData));
            console.log('Successfully stored Baking and Cooling cycles in Redux:', bakingAndCoolingCycleData.length);

            // Fetch and store attachments for offline (same as other sections with files)
            try {
              if (networkStatus && tokenResult?.token && tokenResult.token !== 'offline-temp-token' && plantTourId && !plantTourId.startsWith('temp-')) {
                const sharepointResponse = await instance.acquireTokenSilent({
                  ...loginRequest,
                  account: accounts[0],
                });
                const sharepointToken = sharepointResponse?.accessToken;
                if (sharepointToken) {
                  const { fetchUploadedFiles } = await import('../Services/BakingAndCoolingFileUpload');
                  const files = await fetchUploadedFiles(plantTourId, sharepointToken);
                  console.log('Fetched Baking and Cooling attachments for offline:', files.length);
                  for (const file of files) {
                    const fileName = file.fileName || '';
                    const ext = fileName.split('.').pop()?.toLowerCase() || '';
                    const mimeTypes: Record<string, string> = {
                      jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif',
                      pdf: 'application/pdf', doc: 'application/msword', docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                      xls: 'application/vnd.ms-excel', xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                    };
                    dispatch(addBakingAndCoolingOfflineFile({
                      id: `sp_${file.id}`,
                      cycleNum: file.cycleNum || 1,
                      productName: file.productName,
                      fileData: '',
                      fileName: file.fileName,
                      fileSize: file.fileSize,
                      fileType: mimeTypes[ext] || 'application/octet-stream',
                      timestamp: file.uploadTime,
                      qualityTourId: plantTourId,
                      previewUrl: file.fileUrl || ''
                    }));
                  }
                }
              }
            } catch (attachmentError) {
              console.error('Error fetching Baking and Cooling attachments for offline:', attachmentError);
            }
          } else {
            console.log('No existing Baking and Cooling cycles found on server.');
            dispatch(setBakingAndCoolingFetchedCycles([]));
          }
        }
      } catch (bakingAndCoolingError) {
        console.error('Error loading existing Baking and Cooling cycle data:', bakingAndCoolingError);
        dispatch(setBakingAndCoolingFetchedCycles([]));
      }

      // Step 3.13: Fetch existing Backing Coding completed cycles (same pattern as Baking and Cooling)
      console.log('Fetching existing Backing Coding completed cycles...');
      try {
        if (!networkStatus || !plantTourId || plantTourId.startsWith('temp-')) {
          console.warn('Skipping Backing Coding fetch - network offline or using temporary plant tour ID');
          dispatch(setBackingCodingFetchedCycles([]));
        } else {
          const { fetchCycleData } = await import('../Services/BackingCodingRecord');
          const backingCodingToken = tokenResult?.token && tokenResult.token !== 'offline-temp-token' ? tokenResult.token : undefined;
          const existingBackingCodingCycles = await fetchCycleData(plantTourId, backingCodingToken);
          console.log('Fetched existing Backing Coding cycles:', existingBackingCodingCycles);

          if (existingBackingCodingCycles && existingBackingCodingCycles.length > 0) {
            const backingCodingCycleData = existingBackingCodingCycles.map((cycle: any) => ({
              cycleNum: String(cycle.cycleNum || '').replace('Cycle-', '').trim() || '1',
              product: cycle.product || '',
              executiveName: cycle.executiveName || '',
              sku: cycle.sku || '',
              machineProof: cycle.machineProof || '',
              remarks: cycle.remarks || '',
            }));
            dispatch(setBackingCodingFetchedCycles(backingCodingCycleData));
            console.log('Successfully stored Backing Coding cycles in Redux:', backingCodingCycleData.length);

            try {
              if (networkStatus && tokenResult?.token && tokenResult.token !== 'offline-temp-token' && plantTourId && !plantTourId.startsWith('temp-')) {
                const sharepointResponse = await instance.acquireTokenSilent({
                  ...loginRequest,
                  account: accounts[0],
                });
                const sharepointToken = sharepointResponse?.accessToken;
                if (sharepointToken) {
                  const { fetchUploadedFiles } = await import('../Services/BackingCodingFileUpload');
                  const files = await fetchUploadedFiles(plantTourId, sharepointToken);
                  console.log('Fetched Backing Coding attachments for offline:', files.length);
                  const mimeTypes: Record<string, string> = {
                    jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif',
                    pdf: 'application/pdf', doc: 'application/msword', docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    xls: 'application/vnd.ms-excel', xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                  };
                  for (const file of files) {
                    const ext = (file.fileName || '').split('.').pop()?.toLowerCase() || '';
                    dispatch(addBackingCodingOfflineFile({
                      id: `sp_${file.id}`,
                      cycleNum: file.cycleNum || 1,
                      productName: file.productName,
                      fileData: '',
                      fileName: file.fileName,
                      fileSize: file.fileSize,
                      fileType: mimeTypes[ext] || 'application/octet-stream',
                      timestamp: file.uploadTime,
                      qualityTourId: plantTourId,
                      previewUrl: file.fileUrl || ''
                    }));
                  }
                }
              }
            } catch (attachmentError) {
              console.error('Error fetching Backing Coding attachments for offline:', attachmentError);
            }
          } else {
            console.log('No existing Backing Coding cycles found on server.');
            dispatch(setBackingCodingFetchedCycles([]));
          }
        }
      } catch (backingCodingError) {
        console.error('Error loading existing Backing Coding cycle data:', backingCodingError);
        dispatch(setBackingCodingFetchedCycles([]));
      }

      // Step 3.14: Fetch existing Backing Mixing completed cycles (same pattern as Backing Coding)
      console.log('Fetching existing Backing Mixing completed cycles...');
      try {
        if (!networkStatus || !plantTourId || plantTourId.startsWith('temp-')) {
          console.warn('Skipping Backing Mixing fetch - network offline or using temporary plant tour ID');
          dispatch(setBackingMixingFetchedCycles([]));
        } else {
          const { fetchCycleData } = await import('../Services/BackingMixingRecord');
          const backingMixingToken = tokenResult?.token && tokenResult.token !== 'offline-temp-token' ? tokenResult.token : undefined;
          const existingBackingMixingCycles = await fetchCycleData(plantTourId, backingMixingToken);
          console.log('Fetched existing Backing Mixing cycles:', existingBackingMixingCycles);
          if (existingBackingMixingCycles && existingBackingMixingCycles.length > 0) {
            const backingMixingCycleData = existingBackingMixingCycles.map((cycle: any) => ({
              cycleNum: String(cycle.cycleNum || '').replace('Cycle-', '').trim() || '1',
              product: cycle.product || '',
              executiveName: cycle.executiveName || '',
              lineNo: cycle.lineNo || '',
              startTime: cycle.startTime || '',
              mixingTime: cycle.mixingTime || '',
              waterTemp: cycle.waterTemp || '',
              yeastSlurryTemp: cycle.yeastSlurryTemp || '',
              oilTemp: cycle.oilTemp || '',
              iceKg: cycle.iceKg || '',
              doughTemp: cycle.doughTemp || '',
              dividingWeight10: cycle.dividingWeight10 || '',
              seedVisibility: cycle.seedVisibility || '',
            }));
            dispatch(setBackingMixingFetchedCycles(backingMixingCycleData));
            console.log('Successfully stored Backing Mixing cycles in Redux:', backingMixingCycleData.length);
          } else {
            dispatch(setBackingMixingFetchedCycles([]));
          }
        }
      } catch (backingMixingError) {
        console.error('Error loading existing Backing Mixing cycle data:', backingMixingError);
        dispatch(setBackingMixingFetchedCycles([]));
      }

      // Step 3.15: Fetch existing Process Format completed cycles
      console.log('Fetching existing Process Format completed cycles...');
      try {
        if (!networkStatus || !plantTourId || plantTourId.startsWith('temp-')) {
          console.warn('Skipping Process Format fetch - network offline or using temporary plant tour ID');
          dispatch(setProcessFormatFetchedCycles([]));
        } else {
          const { fetchCycleData } = await import('../Services/ProcessFormatRecord');
          const processFormatToken = tokenResult?.token && tokenResult.token !== 'offline-temp-token' ? tokenResult.token : undefined;
          const existingProcessFormatCycles = await fetchCycleData(plantTourId, processFormatToken);
          console.log('Fetched existing Process Format cycles:', existingProcessFormatCycles);
          if (existingProcessFormatCycles && existingProcessFormatCycles.length > 0) {
            const processFormatCycleData = existingProcessFormatCycles.map((cycle: any) => ({
              cycleNum: String(cycle.cycleNum || '').replace('Cycle-', '').trim() || '1',
              product: cycle.product || '',
              executiveName: cycle.executiveName || '',
              lineNo: cycle.lineNo || '',
              tourstartdate: cycle.tourstartdate || '',
              items: cycle.items || [],
            }));
            dispatch(setProcessFormatFetchedCycles(processFormatCycleData));
            console.log('Successfully stored Process Format cycles in Redux:', processFormatCycleData.length);
          } else {
            dispatch(setProcessFormatFetchedCycles([]));
          }
        }
      } catch (processFormatError) {
        console.error('Error loading existing Process Format cycle data:', processFormatError);
        dispatch(setProcessFormatFetchedCycles([]));
      }

      // Step 3.16: Fetch existing Proofing completed cycles
      console.log('Fetching existing Proofing completed cycles...');
      try {
        if (!networkStatus || !plantTourId || plantTourId.startsWith('temp-')) {
          console.warn('Skipping Proofing fetch - network offline or using temporary plant tour ID');
          dispatch(setProofingFetchedCycles([]));
        } else {
          const { fetchCycleData } = await import('../Services/ProofingRecord');
          const proofingToken = tokenResult?.token && tokenResult.token !== 'offline-temp-token' ? tokenResult.token : undefined;
          const existingProofingCycles = await fetchCycleData(plantTourId, proofingToken);
          console.log('Fetched existing Proofing cycles:', existingProofingCycles);
          if (existingProofingCycles && existingProofingCycles.length > 0) {
            const proofingCycleData = existingProofingCycles.map((cycle: any) => ({
              cycleNum: String(cycle.cycleNum || '').replace('Cycle-', '').trim() || '1',
              product: cycle.product || '',
              executiveName: cycle.executiveName || '',
              lineNumber: cycle.lineNumber || '',
              observedtime: cycle.observedtime || '',
              rows: cycle.rows || [],
            }));
            dispatch(setProofingFetchedCycles(proofingCycleData));
            console.log('Successfully stored Proofing cycles in Redux:', proofingCycleData.length);
          } else {
            dispatch(setProofingFetchedCycles([]));
          }
        }
      } catch (proofingError) {
        console.error('Error loading existing Proofing cycle data:', proofingError);
        dispatch(setProofingFetchedCycles([]));
      }

      // Step 3.17: Fetch existing Baking Sieves And Magnets completed cycles
      console.log('Fetching existing Baking Sieves And Magnets completed cycles...');
      try {
        if (!networkStatus || !plantTourId || plantTourId.startsWith('temp-')) {
          console.warn('Skipping Baking Sieves And Magnets fetch - network offline or using temporary plant tour ID');
          dispatch(setBakingSievesAndMagnetsFetchedCycles([]));
        } else {
          const { fetchCycleData } = await import('../Services/BakingSievesAndMagnetsRecord');
          const bsmToken = tokenResult?.token && tokenResult.token !== 'offline-temp-token' ? tokenResult.token : undefined;
          const existingBSMCycles = await fetchCycleData(plantTourId, bsmToken);
          console.log('Fetched existing Baking Sieves And Magnets cycles:', existingBSMCycles);
          if (existingBSMCycles && existingBSMCycles.length > 0) {
            const bsmCycleData = existingBSMCycles.map((cycle: any) => ({
              cycleNum: String(cycle.cycleNum || '').replace('Cycle-', '').trim() || '1',
              timeValue: cycle.timeValue || '',
              executiveName: cycle.executiveName || '',
              observedtime: cycle.observedtime || '',
              items: cycle.items || [],
            }));
            dispatch(setBakingSievesAndMagnetsFetchedCycles(bsmCycleData));
            console.log('Successfully stored Baking Sieves And Magnets cycles in Redux:', bsmCycleData.length);
          } else {
            dispatch(setBakingSievesAndMagnetsFetchedCycles([]));
          }
        }
      } catch (bsmError) {
        console.error('Error loading existing Baking Sieves And Magnets cycle data:', bsmError);
        dispatch(setBakingSievesAndMagnetsFetchedCycles([]));
      }

      // Step 4: Fetch summary and cycle data APIs - ONLY if online
      console.log('Fetching summary and cycle data...');
      try {
        if (networkStatus && tokenResult && tokenResult.token && tokenResult.token !== 'offline-temp-token' && plantTourId && !plantTourId.startsWith('temp-')) {
          const [summaryData, cycleData] = await Promise.all([
            fetchSummaryData(tokenResult.token, plantTourId).catch(err => {
              console.error('Error fetching summary data:', err);
              return [];
            }),
            fetchCycleDetails(tokenResult.token, plantTourId).catch(err => {
              console.error('Error fetching cycle details:', err);
              return [];
            })
          ]);

          // Handle the API responses properly
          const summaryArray = summaryData && Array.isArray(summaryData) ? summaryData : [];
          const cycleArray = cycleData && Array.isArray(cycleData) ? cycleData : [];

          console.log("Fetched Summary Data:", summaryArray.length, "items");
          console.log("Fetched Cycle Records:", cycleArray.length, "items");

          // Store summary and cycle data in Redux
          dispatch(setSummaryData(summaryArray));
          dispatch(setCycleData(cycleArray));
          dispatch(setLastFetchTimestamp(Date.now()));

          // Process and store category summary and cycle count
          if (summaryArray.length > 0) {
            const processed = processData(summaryArray);
            dispatch(setCategorySummary(processed.summary));
            dispatch(setCycleCount(processed.totalCycles));
            console.log("Category summary and cycle count stored in Redux");
          } else {
            dispatch(setCategorySummary({}));
            dispatch(setCycleCount(0));
          }

          console.log("Summary and cycle data stored in Redux");
        } else {
          console.warn('Skipping summary/cycle data fetch - using temporary token or no plant tour ID');
          // Initialize with empty data
          dispatch(setSummaryData([]));
          dispatch(setCycleData([]));
          dispatch(setCategorySummary({}));
          dispatch(setCycleCount(0));
        }
      } catch (apiError) {
        console.error("Error fetching summary/cycle data:", apiError);
        // Initialize with empty data and continue with offline mode
        dispatch(setSummaryData([]));
        dispatch(setCycleData([]));
        dispatch(setCategorySummary({}));
        dispatch(setCycleCount(0));
      }

      dispatch(setProgress(80));

      // Step 5: Mark as offline mode - CRITICAL: Set this LAST after all API calls complete
      // After this point, NO API calls should be made anywhere in the app
      dispatch(setOfflineCompleted(true));
      dispatch(setOfflineStarted(true)); // THIS PREVENTS ALL FUTURE API CALLS

      dispatch(setProgress(100));
      console.log('=== OFFLINE MODE ACTIVATED ===');
      console.log('All API data fetched and stored in Redux');
      console.log('isOfflineStarted = true - NO MORE API CALLS WILL BE MADE');

    } catch (err) {
      console.error("Error starting offline mode:", err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.error("Error details:", errorMessage);
      
      // If we have at least a plant tour ID, we can still proceed with offline mode
      const currentPlantTourId = store.getState().planTour?.plantTourId;
      if (currentPlantTourId) {
        console.warn('Some API calls failed, but proceeding with offline mode using available data');
        // Still mark as offline mode if we have basic setup
        dispatch(setOfflineCompleted(true));
        dispatch(setOfflineStarted(true));
        dispatch(setProgress(100));
        alert('Offline mode started with limited data. Some features may not be available until you sync.');
      } else {
        // Only show error if we couldn't set up basic offline mode
        alert(`Failed to start offline mode: ${errorMessage}. Please check your connection and try again.`);
      }
    } finally {
      setIsOfflineLoading(false);
    }
  };

  // Department-based handlers for non-Quality Rajpura departments
  const handleOtherDepartmentPlantTour = async () => {
    console.log('=== STARTING PLANT TOUR FOR OTHER DEPARTMENT ===');
    console.log('Department:', getDepartmentName());
    
    try {
      setIsPlantTourLoading(true);
      dispatch(setProgress(0));
      
      // CRITICAL: Check if offline mode is active - if so, skip ALL API calls
      const isOfflineMode = store.getState().appState?.isOfflineStarted || false;
      if (isOfflineMode) {
        console.log('=== OFFLINE MODE ACTIVE - SKIPPING ALL API CALLS ===');
        console.log('Using cached data from Redux for offline plant tour');
        
        // Get cached plant tour ID from Redux
        const cachedPlantTourId = store.getState().planTour?.plantTourId;
        if (cachedPlantTourId) {
          console.log('Using cached plant tour ID:', cachedPlantTourId);
          dispatch(setProgress(100));
          setIsPlantTourLoading(false);
          navigate("/plant-tour-section");
          return; // Exit early - no API calls
        } else {
          throw new Error('No cached plant tour ID found. Please start offline mode first.');
        }
      }
      
      // Check if user has required Name property
      if (!user?.Name) {
        throw new Error('User name not found. Please ensure you are logged in properly.');
      }
      console.log('User name:', user.Name);

      // Step 1: Get access tokens - ONLY if not offline
      console.log('Getting access tokens...');
      dispatch(setProgress(20));
      const tokenResult = await getAccessToken();
      const accessToken = tokenResult?.token;
      
      if (!accessToken) {
        throw new Error('Failed to get access token');
      }

      // Get SharePoint token for employee list
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });
      const sharepointToken = response.accessToken;

      // Step 2: Fetch employee details
      console.log('Fetching employee details...');
      dispatch(setProgress(40));
      const employeeList = await fetchEmployeeList(sharepointToken, user.Name);
      let currentEmployeeDetails: any;
      if (employeeList && employeeList.length > 0) {
        currentEmployeeDetails = employeeList[0];
        console.log('Employee details fetched:', currentEmployeeDetails);
      } else {
        console.warn('Employee not found in EmployeeList. Using fallback for plant tour.');
        currentEmployeeDetails = {
          employeeName: user.Name.trim(),
          departmentId: '135',
          departmentName: 'N/A',
          roleName: 'QA',
          roleId: '1',
          plantId: '1',
          plantName: 'N/A',
        };
      }

      // Step 3: Create or fetch department tour ID
      console.log('Creating/fetching department tour ID...');
      dispatch(setProgress(60));
      const departmentTourId = await createOrFetchDepartmentTour({
        accessToken,
        departmentId: currentEmployeeDetails.departmentId || '135',
        employeeName: currentEmployeeDetails.employeeName || user?.Name || '',
        roleName: currentEmployeeDetails.roleName || 'QA',
        plantId: currentEmployeeDetails.plantId || '1',
        userRoleID: currentEmployeeDetails.roleId || '1',
      });

      if (!departmentTourId) {
        throw new Error('Failed to create or fetch department tour ID');
      }
      console.log('Department tour ID created/fetched:', departmentTourId);

      // Step 4: Store department tour ID in Redux
      console.log('Storing department tour ID in Redux...');
      dispatch(setProgress(80));
      dispatch(setPlantTourId(departmentTourId)); // Store department tour ID as plant tour ID
      console.log('✅ Department Tour ID stored as Plant Tour ID in planTour slice:', departmentTourId);
      
      // Also store in localStorage as backup
      localStorage.setItem('plantTourId', departmentTourId);
      console.log('✅ Department Tour ID stored in localStorage as backup:', departmentTourId);
      
      // Store in plantTourSection slice for consistency
      dispatch(setPlantTourSectionPlantTourId(departmentTourId));
      console.log('✅ Department Tour ID stored in plantTourSection slice:', departmentTourId);

      // Step 5: Store employee details
      dispatch(setEmployeeDetails(currentEmployeeDetails));
      console.log('✅ Employee details stored in Redux');

      // Final completion
      dispatch(setProgress(100));
      
      console.log('=== DEPARTMENT TOUR SETUP COMPLETED ===');
      console.log('Navigating to plant-tour-section...');
      navigate("/plant-tour-section");

    } catch (error) {
      console.error('=== ERROR STARTING PLANT TOUR FOR OTHER DEPARTMENT ===');
      console.error('Error details:', error);
      console.error('Error message:', error instanceof Error ? error.message : 'Unknown error');
      alert(`Failed to start plant tour: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsPlantTourLoading(false);
    }
  };

  const handleOtherDepartmentOfflinePlantTour = () => {
    console.log('Navigating to plant-tour-section for offline plant tour');
    navigate("/plant-tour-section");
  };

  const handleOtherDepartmentStartOfflinePlantTour = async () => {
    console.log('=== STARTING OFFLINE PLANT TOUR FOR OTHER DEPARTMENT ===');
    console.log('Department:', getDepartmentName());
    
    try {
      setIsPlantTourLoading(true);
      setIsOfflineLoading(true);
      dispatch(setProgress(0));
      
      // Check if user has required Name property
      if (!user?.Name) {
        throw new Error('User name not found. Please ensure you are logged in properly.');
      }
      console.log('User name:', user.Name);

      // CRITICAL: Check network status BEFORE making any API calls
      const networkStatus = navigator.onLine;
      console.log('Network status check:', networkStatus ? 'ONLINE' : 'OFFLINE');
      console.log('Internet status:', networkStatus ? 'Online - will attempt API calls' : 'Offline - using fallbacks only');

      if (networkStatus) {
        console.log('=== ONLINE MODE: FETCHING DATA FROM APIS ===');
        
        // Step 1: Get access tokens - ONLY if online
        console.log('Getting access tokens...');
        dispatch(setProgress(10));
        let accessToken: string | null = null;
        let sharepointToken: string | null = null;
        
        if (!networkStatus) {
          // CRITICAL: If offline, skip ALL API calls including getAccessToken and acquireTokenSilent
          console.log('Network is offline - skipping getAccessToken and acquireTokenSilent API calls completely');
          accessToken = 'offline-temp-token';
          sharepointToken = 'offline-temp-token';
        } else {
          // Only call APIs if online
          try {
            const tokenResult = await getAccessToken();
            accessToken = tokenResult?.token || null;
            
            if (!accessToken) {
              console.warn('Failed to get access token, will use fallback');
              accessToken = 'offline-temp-token';
            }

            // Get SharePoint token for employee list
            const response = await instance.acquireTokenSilent({
              ...loginRequest,
              account: accounts[0],
            });
            sharepointToken = response.accessToken;
          } catch (tokenError) {
            console.error('Error getting tokens:', tokenError);
            console.warn('Using fallback tokens for offline mode');
            accessToken = 'offline-temp-token';
            sharepointToken = 'offline-temp-token';
          }
        }

        // Step 2: Fetch employee details - ONLY if online
        console.log('Fetching employee details...');
        dispatch(setProgress(20));
        let currentEmployeeDetails: any;
        if (!networkStatus) {
          // CRITICAL: If offline, skip ALL API calls including fetchEmployeeList
          console.log('Network is offline - skipping fetchEmployeeList API call completely');
          currentEmployeeDetails = {
            employeeName: user.Name.trim(),
            departmentId: '135',
            departmentName: 'N/A',
            roleName: 'QA',
            roleId: '1',
            plantId: '1',
            plantName: 'N/A',
          };
          console.log('Using fallback employee details (offline mode - no API calls):', currentEmployeeDetails);
        } else {
          // Only call APIs if online
          try {
            if (sharepointToken && sharepointToken !== 'offline-temp-token') {
              const employeeList = await fetchEmployeeList(sharepointToken, user.Name);
              if (employeeList && employeeList.length > 0) {
                currentEmployeeDetails = employeeList[0];
                console.log('Employee details fetched:', currentEmployeeDetails);
              } else {
                throw new Error('Employee list empty');
              }
            } else {
              throw new Error('No SharePoint token');
            }
          } catch (employeeError) {
            console.error('Error fetching employee details:', employeeError);
            console.warn('Using fallback employee details for offline plant tour.');
            currentEmployeeDetails = {
              employeeName: user.Name.trim(),
              departmentId: '135',
              departmentName: 'N/A',
              roleName: 'QA',
              roleId: '1',
              plantId: '1',
              plantName: 'N/A',
            };
          }
        }

        // Step 3: Create or fetch plant tour ID (with error handling)
        console.log('Creating/fetching plant tour ID...');
        dispatch(setProgress(30));
        let plantTourId: string | null = null;
        try {
          if (accessToken && accessToken !== 'offline-temp-token') {
            plantTourId = await createOrFetchPlantTour({
              accessToken,
              departmentId: currentEmployeeDetails.departmentId || '135',
              employeeName: currentEmployeeDetails.employeeName || user?.Name || '',
              roleName: currentEmployeeDetails.roleName || 'QA',
              plantId: currentEmployeeDetails.plantId || '1',
              userRoleID: currentEmployeeDetails.roleId || '1',
            });
          }
          
          if (!plantTourId) {
            // Generate temporary ID for offline mode
            plantTourId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            console.warn('Using temporary plant tour ID for offline mode:', plantTourId);
          }
          console.log('Plant tour ID created/fetched:', plantTourId);
        } catch (plantTourError) {
          console.error('Error creating/fetching plant tour ID:', plantTourError);
          plantTourId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
          console.warn('Using temporary plant tour ID for offline mode:', plantTourId);
        }

        // Step 4: Create or fetch department tour ID (with error handling)
        console.log('Creating/fetching department tour ID...');
        dispatch(setProgress(40));
        let departmentTourId: string | null = null;
        try {
          if (accessToken && accessToken !== 'offline-temp-token') {
            departmentTourId = await createOrFetchDepartmentTour({
              accessToken,
              departmentId: currentEmployeeDetails.departmentId || '135',
              employeeName: currentEmployeeDetails.employeeName || user?.Name || '',
              roleName: currentEmployeeDetails.roleName || 'QA',
              plantId: currentEmployeeDetails.plantId || '1',
              userRoleID: currentEmployeeDetails.roleId || '1',
            });
          }
          
          if (!departmentTourId) {
            // Use plant tour ID or generate temporary ID
            departmentTourId = plantTourId || `temp-dept-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            console.warn('Using temporary department tour ID for offline mode:', departmentTourId);
          }
          console.log('Department tour ID created/fetched:', departmentTourId);
        } catch (deptTourError) {
          console.error('Error creating/fetching department tour ID:', deptTourError);
          departmentTourId = plantTourId || `temp-dept-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
          console.warn('Using temporary department tour ID for offline mode:', departmentTourId);
        }

        // Step 5: Fetch criteria master list - ONLY if online
        console.log('Fetching criteria master list...');
        dispatch(setProgress(50));
        const plantName = currentEmployeeDetails?.plantName || currentEmployeeDetails?.PlantName || '';
        const departmentName = currentEmployeeDetails?.departmentName || currentEmployeeDetails?.DepartmentName || '';
        
        // Debug logging for Production - Old Plant department
        console.log('=== HOMEPAGE OFFLINE SETUP DEBUGGING ===');
        console.log('Plant Name:', plantName);
        console.log('Department Name:', departmentName);
        console.log('Employee Details:', currentEmployeeDetails);
        
        let criteriaList: any[] = [];
        try {
          if (networkStatus && sharepointToken && sharepointToken !== 'offline-temp-token') {
            // For Plant Tour Section, we want to show ALL areas for the department, not just the employee's specific area
            // So we pass undefined for areaName to get all areas
            criteriaList = await CriteriaMasterService.fetchCriteriaMasterList(
              sharepointToken,
              plantName,
              departmentName,
              undefined // Don't filter by area - show all areas for the department
            );
            console.log('Criteria master list fetched:', criteriaList.length, 'items');
          } else {
            console.warn('Skipping criteria master list fetch - network offline or using temporary token');
            criteriaList = [];
          }
        } catch (criteriaError) {
          console.error('Error fetching criteria master list:', criteriaError);
          criteriaList = [];
        }

        // Step 6: Fetch existing observations - ONLY if online
        console.log('Fetching existing observations...');
        dispatch(setProgress(60));
        let existingObservations: any[] = [];
        try {
          if (networkStatus && departmentTourId && !departmentTourId.startsWith('temp-')) {
            existingObservations = await PlantTourService.fetchExistingObservations(departmentTourId);
            console.log('Existing observations fetched:', existingObservations.length, 'items');
          } else {
            console.warn('Skipping observations fetch - network offline or using temporary tour ID');
            existingObservations = [];
          }
        } catch (observationsError) {
          console.error('Error fetching existing observations:', observationsError);
          existingObservations = [];
        }

        // Step 7: Store all data in Redux
        console.log('Storing all data in Redux...');
        dispatch(setProgress(80));
        
        // For non-Quality departments, use departmentTourId as the plantTourId
        // This ensures the PlantTourSection component can find the correct tour ID
        const finalPlantTourId = departmentTourId || plantTourId;
        dispatch(setPlantTourId(finalPlantTourId)); // Store department tour ID as plant tour ID in planTour slice
        console.log('✅ Department Tour ID stored as Plant Tour ID in planTour slice:', finalPlantTourId);
        
        // CRITICAL: Also store in localStorage as backup
        localStorage.setItem('plantTourId', finalPlantTourId);
        localStorage.setItem('offlinePlantTourActive', 'true');
        console.log('✅ Final Plant Tour ID stored in localStorage as backup:', finalPlantTourId);
        
        dispatch(setOfflineCriteriaList(criteriaList));
        dispatch(setOfflineEmployeeDetails(currentEmployeeDetails));
        dispatch(setOfflineExistingObservations(existingObservations));
        dispatch(setOfflineDataTimestamp(Date.now()));
        
        // Also store in plantTourSection slice for consistency
        dispatch(setPlantTourSectionPlantTourId(finalPlantTourId)); // Store in plantTourSection slice
        console.log('✅ Final Plant Tour ID stored in plantTourSection slice:', finalPlantTourId);

        // Step 7.5: Fetch Baking and Cooling cycles + attachments so offline Baking & Cooling view matches online (Bakery Noida and any department that uses this section)
        if (networkStatus && finalPlantTourId && !finalPlantTourId.startsWith('temp-') && accessToken && accessToken !== 'offline-temp-token') {
          try {
            const { fetchCycleData } = await import('../Services/BakingAndCoolingRecord');
            const bakingCoolingToken = accessToken;
            const existingBakingAndCoolingCycles = await fetchCycleData(finalPlantTourId, bakingCoolingToken);
            if (existingBakingAndCoolingCycles && existingBakingAndCoolingCycles.length > 0) {
              const bakingAndCoolingCycleData = existingBakingAndCoolingCycles.map((cycle: any) => {
                let cycleNumStr = String(cycle.cycleNum || '').replace('Cycle-', '').trim();
                if (!cycleNumStr || cycleNumStr === '') cycleNumStr = '1';
                return {
                  cycleNum: cycleNumStr,
                  product: cycle.product || '',
                  executiveName: cycle.executiveName || '',
                  bakingTime: cycle.bakingTime || '',
                  lineNumber: cycle.lineNumber || '',
                  bakingTempZone1: cycle.bakingtempzone1 || '',
                  bakingTempZone2: cycle.bakingtempzone2 || '',
                  bakingTempZone3: cycle.bakingtempzone3 || '',
                  bakingTempZone4: cycle.bakingtempzone4 || '',
                  productTempAfterBaking: cycle.producttempafterbaking || '',
                  ap: cycle.pccf_ap || '',
                  std: cycle.pccf_std || '',
                  ad: cycle.pccf_ad || '',
                  ovenFireStartTime: cycle.ovenfirestarttime || '',
                  ovenStopTime: cycle.ovenstoptime || '',
                  productInTime: cycle.productintime || '',
                  productOutTime: cycle.productouttime || '',
                  coolingTime: cycle.coolingtime || '',
                  coolingTemp: cycle.coolingtemp || '',
                  coreTemp: cycle.coretemp || '',
                  productColor: cycle.productcolor || ''
                };
              });
              dispatch(setBakingAndCoolingFetchedCycles(bakingAndCoolingCycleData));
              console.log('✅ Baking and Cooling cycles stored for offline:', bakingAndCoolingCycleData.length);
              try {
                const sharepointResponse = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
                const sharepointToken = sharepointResponse?.accessToken;
                if (sharepointToken) {
                  const { fetchUploadedFiles } = await import('../Services/BakingAndCoolingFileUpload');
                  const files = await fetchUploadedFiles(finalPlantTourId, sharepointToken);
                  const mimeTypes: Record<string, string> = {
                    jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif',
                    pdf: 'application/pdf', doc: 'application/msword', docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    xls: 'application/vnd.ms-excel', xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                  };
                  for (const file of files) {
                    const ext = (file.fileName || '').split('.').pop()?.toLowerCase() || '';
                    dispatch(addBakingAndCoolingOfflineFile({
                      id: `sp_${file.id}`,
                      cycleNum: file.cycleNum || 1,
                      productName: file.productName,
                      fileData: '',
                      fileName: file.fileName,
                      fileSize: file.fileSize,
                      fileType: mimeTypes[ext] || 'application/octet-stream',
                      timestamp: file.uploadTime,
                      qualityTourId: finalPlantTourId,
                      previewUrl: file.fileUrl || ''
                    }));
                  }
                  console.log('✅ Baking and Cooling attachments stored for offline:', files.length);
                }
              } catch (attErr) {
                console.warn('Baking and Cooling attachments fetch for offline failed:', attErr);
              }
            } else {
              dispatch(setBakingAndCoolingFetchedCycles([]));
            }
          } catch (bcErr) {
            console.warn('Baking and Cooling cycles fetch for offline failed:', bcErr);
            dispatch(setBakingAndCoolingFetchedCycles([]));
          }
        }

        // Step 7.6: Fetch Backing Coding cycles + attachments for offline
        if (networkStatus && finalPlantTourId && !finalPlantTourId.startsWith('temp-') && accessToken && accessToken !== 'offline-temp-token') {
          try {
            const { fetchCycleData } = await import('../Services/BackingCodingRecord');
            const existingBackingCodingCycles = await fetchCycleData(finalPlantTourId, accessToken);
            if (existingBackingCodingCycles && existingBackingCodingCycles.length > 0) {
              const backingCodingCycleData = existingBackingCodingCycles.map((cycle: any) => ({
                cycleNum: String(cycle.cycleNum || '').replace('Cycle-', '').trim() || '1',
                product: cycle.product || '',
                executiveName: cycle.executiveName || '',
                sku: cycle.sku || '',
                machineProof: cycle.machineProof || '',
                remarks: cycle.remarks || '',
              }));
              dispatch(setBackingCodingFetchedCycles(backingCodingCycleData));
              console.log('✅ Backing Coding cycles stored for offline:', backingCodingCycleData.length);
              try {
                const sharepointResponse = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
                const sharepointToken = sharepointResponse?.accessToken;
                if (sharepointToken) {
                  const { fetchUploadedFiles } = await import('../Services/BackingCodingFileUpload');
                  const files = await fetchUploadedFiles(finalPlantTourId, sharepointToken);
                  const mimeTypes: Record<string, string> = {
                    jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif',
                    pdf: 'application/pdf', doc: 'application/msword', docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    xls: 'application/vnd.ms-excel', xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                  };
                  for (const file of files) {
                    const ext = (file.fileName || '').split('.').pop()?.toLowerCase() || '';
                    dispatch(addBackingCodingOfflineFile({
                      id: `sp_${file.id}`,
                      cycleNum: file.cycleNum || 1,
                      productName: file.productName,
                      fileData: '',
                      fileName: file.fileName,
                      fileSize: file.fileSize,
                      fileType: mimeTypes[ext] || 'application/octet-stream',
                      timestamp: file.uploadTime,
                      qualityTourId: finalPlantTourId,
                      previewUrl: file.fileUrl || ''
                    }));
                  }
                  console.log('✅ Backing Coding attachments stored for offline:', files.length);
                }
              } catch (attErr) {
                console.warn('Backing Coding attachments fetch for offline failed:', attErr);
              }
            } else {
              dispatch(setBackingCodingFetchedCycles([]));
            }
          } catch (bcErr) {
            console.warn('Backing Coding cycles fetch for offline failed:', bcErr);
            dispatch(setBackingCodingFetchedCycles([]));
          }
        }

        // Step 7.7: Fetch Backing Mixing cycles for offline
        if (networkStatus && finalPlantTourId && !finalPlantTourId.startsWith('temp-') && accessToken && accessToken !== 'offline-temp-token') {
          try {
            const { fetchCycleData } = await import('../Services/BackingMixingRecord');
            const existingBackingMixingCycles = await fetchCycleData(finalPlantTourId, accessToken);
            if (existingBackingMixingCycles && existingBackingMixingCycles.length > 0) {
              const backingMixingCycleData = existingBackingMixingCycles.map((cycle: any) => ({
                cycleNum: String(cycle.cycleNum || '').replace('Cycle-', '').trim() || '1',
                product: cycle.product || '',
                executiveName: cycle.executiveName || '',
                lineNo: cycle.lineNo || '',
                startTime: cycle.startTime || '',
                mixingTime: cycle.mixingTime || '',
                waterTemp: cycle.waterTemp || '',
                yeastSlurryTemp: cycle.yeastSlurryTemp || '',
                oilTemp: cycle.oilTemp || '',
                iceKg: cycle.iceKg || '',
                doughTemp: cycle.doughTemp || '',
                dividingWeight10: cycle.dividingWeight10 || '',
                seedVisibility: cycle.seedVisibility || '',
              }));
              dispatch(setBackingMixingFetchedCycles(backingMixingCycleData));
              console.log('✅ Backing Mixing cycles stored for offline:', backingMixingCycleData.length);
            } else {
              dispatch(setBackingMixingFetchedCycles([]));
            }
          } catch (bmErr) {
            console.warn('Backing Mixing cycles fetch for offline failed:', bmErr);
            dispatch(setBackingMixingFetchedCycles([]));
          }
        }

        // Step 7.8: Fetch Process Format cycles for offline
        if (networkStatus && finalPlantTourId && !finalPlantTourId.startsWith('temp-') && accessToken && accessToken !== 'offline-temp-token') {
          try {
            const { fetchCycleData } = await import('../Services/ProcessFormatRecord');
            const existingProcessFormatCycles = await fetchCycleData(finalPlantTourId, accessToken);
            if (existingProcessFormatCycles && existingProcessFormatCycles.length > 0) {
              const processFormatCycleData = existingProcessFormatCycles.map((cycle: any) => ({
                cycleNum: String(cycle.cycleNum || '').replace('Cycle-', '').trim() || '1',
                product: cycle.product || '',
                executiveName: cycle.executiveName || '',
                lineNo: cycle.lineNo || '',
                tourstartdate: cycle.tourstartdate || '',
                items: cycle.items || [],
              }));
              dispatch(setProcessFormatFetchedCycles(processFormatCycleData));
              console.log('✅ Process Format cycles stored for offline:', processFormatCycleData.length);
            } else {
              dispatch(setProcessFormatFetchedCycles([]));
            }
          } catch (pfErr) {
            console.warn('Process Format cycles fetch for offline failed:', pfErr);
            dispatch(setProcessFormatFetchedCycles([]));
          }
        }

        // Step 7.9: Fetch Proofing cycles for offline
        if (networkStatus && finalPlantTourId && !finalPlantTourId.startsWith('temp-') && accessToken && accessToken !== 'offline-temp-token') {
          try {
            const { fetchCycleData } = await import('../Services/ProofingRecord');
            const existingProofingCycles = await fetchCycleData(finalPlantTourId, accessToken);
            if (existingProofingCycles && existingProofingCycles.length > 0) {
              const proofingCycleData = existingProofingCycles.map((cycle: any) => ({
                cycleNum: String(cycle.cycleNum || '').replace('Cycle-', '').trim() || '1',
                product: cycle.product || '',
                executiveName: cycle.executiveName || '',
                lineNumber: cycle.lineNumber || '',
                observedtime: cycle.observedtime || '',
                rows: cycle.rows || [],
              }));
              dispatch(setProofingFetchedCycles(proofingCycleData));
              console.log('✅ Proofing cycles stored for offline:', proofingCycleData.length);
            } else {
              dispatch(setProofingFetchedCycles([]));
            }
          } catch (proofErr) {
            console.warn('Proofing cycles fetch for offline failed:', proofErr);
            dispatch(setProofingFetchedCycles([]));
          }
        }

        // Step 7.10: Fetch Baking Sieves And Magnets cycles for offline
        if (networkStatus && finalPlantTourId && !finalPlantTourId.startsWith('temp-') && accessToken && accessToken !== 'offline-temp-token') {
          try {
            const { fetchCycleData } = await import('../Services/BakingSievesAndMagnetsRecord');
            const existingBSMCycles = await fetchCycleData(finalPlantTourId, accessToken);
            if (existingBSMCycles && existingBSMCycles.length > 0) {
              const bsmCycleData = existingBSMCycles.map((cycle: any) => ({
                cycleNum: String(cycle.cycleNum || '').replace('Cycle-', '').trim() || '1',
                timeValue: cycle.timeValue || '',
                executiveName: cycle.executiveName || '',
                observedtime: cycle.observedtime || '',
                items: cycle.items || [],
              }));
              dispatch(setBakingSievesAndMagnetsFetchedCycles(bsmCycleData));
              console.log('✅ Baking Sieves And Magnets cycles stored for offline:', bsmCycleData.length);
            } else {
              dispatch(setBakingSievesAndMagnetsFetchedCycles([]));
            }
          } catch (bsmErr) {
            console.warn('Baking Sieves And Magnets cycles fetch for offline failed:', bsmErr);
            dispatch(setBakingSievesAndMagnetsFetchedCycles([]));
          }
        }

        // Step 8: Mark as offline mode
        dispatch(setProgress(90));
        dispatch(setOfflineCompleted(true));
        dispatch(setOfflineStarted(true));
        dispatch(setOfflineMode(true));

        console.log('=== ONLINE DATA FETCH COMPLETED ===');
        console.log('Plant Tour ID:', plantTourId);
        console.log('Department Tour ID:', departmentTourId);
        console.log('Final Plant Tour ID (stored in Redux):', finalPlantTourId);
        console.log('Criteria Count:', criteriaList.length);
        console.log('Employee Details:', currentEmployeeDetails ? 'Available' : 'Not found');
        console.log('Existing Observations Count:', existingObservations.length);
        
        // Verify Redux state after storing
        const currentState = store.getState();
        console.log('🔍 Redux State Verification:');
        console.log('  - planTour.plantTourId:', currentState.planTour.plantTourId);
        console.log('  - plantTourSection.plantTourId:', currentState.plantTourSection.plantTourId);
        console.log('  - planTour.offlineCriteriaList.length:', currentState.planTour.offlineCriteriaList.length);
        console.log('  - planTour.offlineEmployeeDetails:', currentState.planTour.offlineEmployeeDetails ? 'Available' : 'Not found');
        
        // CRITICAL: Check if finalPlantTourId is actually stored
        if (!currentState.planTour.plantTourId) {
          console.error('❌ CRITICAL ERROR: finalPlantTourId is NOT stored in Redux state!');
          console.error('This will cause the "Plant tour ID not found" error!');
        } else {
          console.log('✅ SUCCESS: finalPlantTourId is stored in Redux state:', currentState.planTour.plantTourId);
        }

      } else {
        console.log('=== OFFLINE MODE: USING CACHED REDUX DATA ===');
        dispatch(setProgress(20));
        
        // Check if we have cached data
        const planTourState = useSelector((state: any) => state.planTour);
        
        if (!planTourState.offlineCriteriaList.length) {
          throw new Error('No offline data available. Please connect to internet to fetch data first.');
        }

        console.log('Using cached data from Redux');
        console.log('Cached criteria count:', planTourState.offlineCriteriaList.length);
        console.log('Cached employee details:', planTourState.offlineEmployeeDetails ? 'Available' : 'Not found');
        console.log('Cached observations count:', planTourState.offlineExistingObservations.length);

        // Mark as offline mode
        dispatch(setProgress(80));
        dispatch(setOfflineCompleted(true));
        dispatch(setOfflineStarted(true));
        dispatch(setOfflineMode(true));
      }

      // Final completion
      dispatch(setProgress(100));
      
      // Don't navigate - just complete the setup
      console.log('=== OFFLINE PLANT TOUR SETUP COMPLETED ===');
      console.log('Button will now change to "Offline Plant Tour"');

    } catch (error) {
      console.error('=== ERROR STARTING OFFLINE PLANT TOUR ===');
      console.error('Error details:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Error message:', errorMessage);
      
      // If we have at least a plant tour ID, we can still proceed with offline mode
      const currentState = store.getState();
      const currentPlantTourId = currentState.planTour?.plantTourId;
      if (currentPlantTourId) {
        console.warn('Some API calls failed, but proceeding with offline mode using available data');
        // Still mark as offline mode if we have basic setup
        dispatch(setOfflineCompleted(true));
        dispatch(setOfflineStarted(true));
        dispatch(setOfflineMode(true));
        dispatch(setProgress(100));
        alert('Offline mode started with limited data. Some features may not be available until you sync.');
      } else {
        // Only show error if we couldn't set up basic offline mode
        alert(`Failed to start offline plant tour: ${errorMessage}. Please check your connection and try again.`);
      }
    } finally {
      setIsPlantTourLoading(false);
      setIsOfflineLoading(false);
    }
  };

  const handleOtherDepartmentSyncCancelOffline = async () => {
    console.log('=== CHECKING FOR OFFLINE DATA TO SYNC ===');
    
    try {
      // Check if there's any offline data to sync
      const hasOfflineData = hasOfflineDataToSync();
      
      // Check for Plant Tour Section offline data
      const { getOfflineToursForSync } = await import('../Services/PlantTourOfflineStorage');
      const plantTourOfflineData = getOfflineToursForSync();
      const hasPlantTourOfflineData = plantTourOfflineData.length > 0;
      
      console.log('General offline data available:', hasOfflineData);
      console.log('Plant Tour Section offline data available:', hasPlantTourOfflineData);
      console.log('Plant Tour Section offline data count:', plantTourOfflineData.length);
      
      // Also consider Baking and Cooling and other section offline data (totalOfflineCount includes all sections)
      const hasAnySectionOfflineData = totalOfflineCount > 0;
      if (!hasOfflineData && !hasPlantTourOfflineData && !hasAnySectionOfflineData) {
        console.log('No offline data found to sync. Canceling offline mode...');
        
        // Clear offline data from localStorage
        clearAllOfflineData();
        
        // Clear offline mode and all related Redux state
        dispatch(setOfflineMode(false));
        dispatch(clearOfflineData()); // Clears offlineCriteriaList, offlineEmployeeDetails, etc.
        dispatch(setOfflineStarted(false));
        dispatch(setOfflineCompleted(false));
        dispatch(resetOfflineState()); // Resets progress and other offline state
        
        // Clear Plant Tour Section specific Redux state
        dispatch(setOfflineCriteriaList([]));
        dispatch(setOfflineEmployeeDetails(null));
        dispatch(setOfflineExistingObservations([]));
        dispatch(setOfflineDataTimestamp(0));
        
        console.log('All offline data cleared from localStorage and Redux state');
        alert('No offline data found. Offline mode cancelled.');
        return;
      }

      // If there is offline data, sync it
      console.log('Offline data found. Starting sync process...');
      
      // Check if internet is available
      if (!isOnline) {
        console.log('No internet connection available for syncing');
        alert('⚠️ Internet connection required to sync offline data. Please check your connection and try again.');
        return;
      }

      // Get access token for syncing (forceForSync so we get token even while offline mode is still active)
      const tokenResult = await getAccessToken(undefined, undefined, true);
      const accessToken = tokenResult?.token;

      if (!accessToken) {
        console.error('No access token available for syncing');
        alert('❌ Authentication error. Please log in again.');
        return;
      }

      let totalSynced = 0;
      let totalErrors = 0;

      // Sync Plant Tour Section offline data
      if (hasPlantTourOfflineData) {
        console.log('=== SYNCING PLANT TOUR SECTION OFFLINE DATA ===');
        console.log('Plant Tour Section offline data:', plantTourOfflineData);
        
        try {
          for (const tourData of plantTourOfflineData) {
            console.log(`Syncing Plant Tour Section data for tour: ${tourData.plantTourId}`);
            console.log(`Observations count: ${tourData.observations.length}`);
            
            for (const observation of tourData.observations) {
              try {
                console.log(`Syncing observation: ${observation.id}`, {
                  sectionName: observation.sectionName,
                  questionId: observation.questionId,
                  response: observation.response,
                  isNotApplicable: observation.isNotApplicable
                });

                if (observation.isNotApplicable) {
                  // Sync Not Applicable observation
                  console.log('Syncing Not Applicable observation with data:', {
                    criteria: observation.observationData?.criteria,
                    employeeDetails: observation.employeeDetails,
                    user: observation.user,
                    plantTourId: observation.plantTourId,
                    sectionName: observation.sectionName
                  });
                  
                  const { saveNotApplicableObservation } = await import('../Services/NotApplicableObservationService');
                  await saveNotApplicableObservation(
                    observation.observationData?.criteria || {},
                    observation.employeeDetails,
                    observation.user,
                    observation.plantTourId,
                    observation.sectionName,
                    { forceTokenRefresh: true }
                  );
                } else {
                  // Sync regular observation
                  console.log('Syncing regular observation with data:', observation.observationData);
                  await PlantTourService.saveObservationToAPI(observation.observationData, { forceTokenRefresh: true });
                }
                
                console.log(`Successfully synced observation: ${observation.id}`);
                totalSynced++;
              } catch (observationError) {
                console.error(`Failed to sync observation ${observation.id}:`, observationError);
                totalErrors++;
              }
            }
          }
          
          console.log('Plant Tour Section offline data sync completed');
        } catch (error) {
          console.error('Error syncing Plant Tour Section offline data:', error);
          totalErrors++;
        }
      }

      // Sync pause data if available
      try {
        const offlineData = JSON.parse(localStorage.getItem('plantTourOfflineData') || '{}');
        if (offlineData.pauseData) {
          console.log('=== SYNCING PAUSE DATA ===');
          console.log('Pause data found:', offlineData.pauseData);
          
          try {
            // Sync pause data using PlantTourService
            await PlantTourService.updateTourStatus(
              offlineData.pauseData.plantTourId, 
              offlineData.pauseData.tourUpdateData,
              { forceTokenRefresh: true }
            );
            
            console.log('Successfully synced pause data');
            totalSynced++;
            
            // Remove pause data from localStorage after successful sync
            delete offlineData.pauseData;
            localStorage.setItem('plantTourOfflineData', JSON.stringify(offlineData));
            
          } catch (pauseError) {
            console.error('Failed to sync pause data:', pauseError);
            totalErrors++;
          }
        }
      } catch (error) {
        console.error('Error checking for pause data:', error);
      }

      // Sync finish data if available
      try {
        const offlineData = JSON.parse(localStorage.getItem('plantTourOfflineData') || '{}');
        if (offlineData.finishData) {
          console.log('=== SYNCING FINISH DATA ===');
          console.log('Finish data found:', offlineData.finishData);
          
          try {
            // Import and use FinishTourService
            const { finishPlantTour } = await import('../Services/FinishTourService');
            
            // Sync finish data
            await finishPlantTour(
              offlineData.finishData.plantTourId,
              offlineData.finishData.validationData,
              offlineData.finishData.finalComment,
              { forceTokenRefresh: true }
            );
            
            console.log('Successfully synced finish data');
            totalSynced++;
            
            // Remove finish data from localStorage after successful sync
            delete offlineData.finishData;
            localStorage.setItem('plantTourOfflineData', JSON.stringify(offlineData));
            
          } catch (finishError) {
            console.error('Failed to sync finish data:', finishError);
            totalErrors++;
          }
        }
      } catch (error) {
        console.error('Error checking for finish data:', error);
      }

      // Sync Baking and Cooling offline data (so "other department" sync includes B&C when present)
      const bcOfflineData = store.getState()?.bakingAndCooling?.offlineSavedData || [];
      const bcOfflineFiles = store.getState()?.bakingAndCooling?.offlineFiles || [];
      if (bcOfflineData.length > 0) {
        console.log('=== SYNCING BAKING AND COOLING OFFLINE DATA (other dept) ===');
        try {
          for (const data of bcOfflineData) {
            try {
              const cycleNo = data.cycleNo;
              const plantTourId = planTourState?.plantTourId;
              const userName = user?.Name || 'Current User';
              const storedStr = localStorage.getItem(`baking-cooling-cycle-${cycleNo}-data`);
              const stored = storedStr ? JSON.parse(storedStr) : null;
              if (stored && Array.isArray(stored) && stored.length > 0) {
                const { savesectionApicall } = await import('../Services/BakingAndCoolingRecord');
                const result = await savesectionApicall(stored, accessToken);
                if (result.success) {
                  totalSynced++;
                  localStorage.removeItem(`baking-cooling-cycle-${cycleNo}-data`);
                } else {
                  throw new Error(`Failed to sync Baking and Cooling cycle ${cycleNo}: ${result.message}`);
                }
              } else if (data.records && data.records.length > 0) {
                const record = data.records[0];
                const { collectEstimationDataCycleSave, savesectionApicall } = await import('../Services/BakingAndCoolingRecord');
                const { savedData } = await collectEstimationDataCycleSave(
                  cycleNo,
                  {
                    productName: record.product || '',
                    lineNumber: record.lineNumber || '',
                    executiveName: record.executiveName || '',
                    bakingTime: record.bakingTime || ''
                  },
                  {
                    bakingTempZone1: record.bakingTempZone1 || '',
                    bakingTempZone2: record.bakingTempZone2 || '',
                    bakingTempZone3: record.bakingTempZone3 || '',
                    bakingTempZone4: record.bakingTempZone4 || '',
                    productTempAfterBaking: record.productTempAfterBaking || '',
                    ap: record.ap || '',
                    std: record.std || '',
                    ad: record.ad || '',
                    ovenFireStartTime: record.ovenFireStartTime || '',
                    ovenStopTime: record.ovenStopTime || '',
                    productInTime: record.productInTime || '',
                    productOutTime: record.productOutTime || '',
                    coolingTime: record.coolingTime || '',
                    coolingTemp: record.coolingTemp || '',
                    coreTemp: record.coreTemp || '',
                    productColor: record.productColor || ''
                  },
                  plantTourId || 'N/A',
                  userName
                );
                const result = await savesectionApicall(savedData, accessToken);
                if (result.success) {
                  totalSynced++;
                } else {
                  throw new Error(`Failed to sync Baking and Cooling cycle ${cycleNo}: ${result.message}`);
                }
              }
            } catch (bcError) {
              console.error(`Error syncing Baking and Cooling cycle ${data.cycleNo}:`, bcError);
              totalErrors++;
            }
          }
        } catch (error) {
          console.error('Error syncing Baking and Cooling offline data:', error);
          totalErrors++;
        }
      }
      if (bcOfflineFiles.length > 0) {
        try {
          const fileToken = await getMsalToken(instance, accounts);
          if (fileToken) {
            const { syncOfflineFiles } = await import('../Services/BakingAndCoolingOfflineFileUpload');
            const fileSyncResult = await syncOfflineFiles(fileToken);
            if (fileSyncResult.success) {
              totalSynced += bcOfflineFiles.length;
            } else {
              totalErrors += bcOfflineFiles.length;
            }
          } else {
            totalErrors += bcOfflineFiles.length;
          }
        } catch (error) {
          console.error('Error syncing Baking and Cooling offline files:', error);
          totalErrors++;
        }
      }

      // Sync Backing Coding offline data (one API call per cycle with all products, same as Baking and Cooling)
      const backingCodingOfflineData = store.getState()?.backingCoding?.offlineSavedData || [];
      const backingCodingOfflineFiles = store.getState()?.backingCoding?.offlineFiles || [];
      if (backingCodingOfflineData.length > 0) {
        console.log('=== SYNCING BACKING CODING OFFLINE DATA ===');
        try {
          const uniqueCycleNos = [...new Set(backingCodingOfflineData.map((d: any) => d.cycleNo))];
          for (const cycleNo of uniqueCycleNos) {
            try {
              const storedStr = localStorage.getItem(`backing-coding-cycle-${cycleNo}-data`);
              const stored = storedStr ? JSON.parse(storedStr) : null;
              if (stored && Array.isArray(stored) && stored.length > 0) {
                const { savesectionApicall } = await import('../Services/BackingCodingRecord');
                const result = await savesectionApicall(stored, accessToken);
                if (result.success) {
                  totalSynced += stored.length;
                  localStorage.removeItem(`backing-coding-cycle-${cycleNo}-data`);
                } else {
                  throw new Error(`Failed to sync Backing Coding cycle ${cycleNo}: ${result.message}`);
                }
              } else {
                const dataForCycle = backingCodingOfflineData.filter((d: any) => d.cycleNo === cycleNo);
                const plantTourIdForSync = planTourState?.plantTourId;
                const userName = user?.Name || 'Current User';
                const { collectBackingCodingRecord, savesectionApicall } = await import('../Services/BackingCodingRecord');
                const records = dataForCycle.flatMap((d: any) =>
                  (d.records || []).map((r: any) =>
                    collectBackingCodingRecord(
                      cycleNo,
                      r.product || '',
                      r.executiveName || '',
                      r.sku || null,
                      r.machineProof || null,
                      r.remarks || null,
                      plantTourIdForSync || 'N/A',
                      userName,
                      null
                    )
                  )
                );
                if (records.length > 0) {
                  const result = await savesectionApicall(records, accessToken);
                  if (result.success) totalSynced += records.length;
                  else throw new Error(`Failed to sync Backing Coding cycle ${cycleNo}: ${result.message}`);
                }
              }
            } catch (bcError) {
              console.error(`Error syncing Backing Coding cycle ${cycleNo}:`, bcError);
              totalErrors++;
            }
          }
        } catch (error) {
          console.error('Error syncing Backing Coding offline data:', error);
          totalErrors++;
        }
      }
      // Only sync files with fileData (uploaded while offline), not fetched files
      const backingCodingFilesToSync = backingCodingOfflineFiles.filter((f: any) => f.fileData && f.fileData.trim() !== '');
      if (backingCodingFilesToSync.length > 0) {
        try {
          const fileToken = await getMsalToken(instance, accounts);
          if (fileToken) {
            const { syncOfflineFiles } = await import('../Services/BackingCodingOfflineFileUpload');
            const fileSyncResult = await syncOfflineFiles(fileToken);
            if (fileSyncResult.success) {
              totalSynced += backingCodingFilesToSync.length;
            } else {
              totalErrors += backingCodingFilesToSync.length;
            }
          } else {
            totalErrors += backingCodingFilesToSync.length;
          }
        } catch (error) {
          console.error('Error syncing Backing Coding offline files:', error);
          totalErrors++;
        }
      }

      // Sync Backing Mixing offline data (one API call per cycle with all products)
      const backingMixingOfflineDataSync = store.getState()?.backingMixing?.offlineSavedData || [];
      if (backingMixingOfflineDataSync.length > 0) {
        console.log('=== SYNCING BACKING MIXING OFFLINE DATA ===');
        try {
          const uniqueCycleNos = [...new Set(backingMixingOfflineDataSync.map((d: any) => d.cycleNo))];
          for (const cycleNo of uniqueCycleNos) {
            try {
              const storedStr = localStorage.getItem(`backing-mixing-cycle-${cycleNo}-data`);
              const stored = storedStr ? JSON.parse(storedStr) : null;
              if (stored && Array.isArray(stored) && stored.length > 0) {
                const { savesectionApicall } = await import('../Services/BackingMixingRecord');
                const result = await savesectionApicall(stored, accessToken);
                if (result.success) {
                  totalSynced += stored.length;
                  localStorage.removeItem(`backing-mixing-cycle-${cycleNo}-data`);
                } else {
                  throw new Error(`Failed to sync Backing Mixing cycle ${cycleNo}: ${result.message}`);
                }
              } else {
                const dataForCycle = backingMixingOfflineDataSync.filter((d: any) => d.cycleNo === cycleNo);
                const plantTourIdForSync = planTourState?.plantTourId;
                const userName = user?.Name || 'Current User';
                const { collectBackingMixingRecord, savesectionApicall } = await import('../Services/BackingMixingRecord');
                const records = dataForCycle.flatMap((d: any) =>
                  (d.records || []).map((r: any) =>
                    collectBackingMixingRecord(
                      cycleNo,
                      r.product || '',
                      r.executiveName || '',
                      r.lineNo || null,
                      r.startTime || null,
                      r.mixingTime || null,
                      r.waterTemp || null,
                      r.yeastSlurryTemp || null,
                      r.oilTemp || null,
                      r.iceKg || null,
                      r.doughTemp || null,
                      r.dividingWeight10 || null,
                      r.seedVisibility || null,
                      plantTourIdForSync || 'N/A',
                      userName
                    )
                  )
                );
                if (records.length > 0) {
                  const result = await savesectionApicall(records, accessToken);
                  if (result.success) totalSynced += records.length;
                  else throw new Error(`Failed to sync Backing Mixing cycle ${cycleNo}: ${result.message}`);
                }
              }
            } catch (bmError) {
              console.error(`Error syncing Backing Mixing cycle ${cycleNo}:`, bmError);
              totalErrors++;
            }
          }
        } catch (error) {
          console.error('Error syncing Backing Mixing offline data:', error);
          totalErrors++;
        }
      }

      // Sync Process Format offline data
      const processFormatOfflineDataSync = store.getState()?.processFormat?.offlineSavedData || [];
      if (processFormatOfflineDataSync.length > 0) {
        console.log('=== SYNCING PROCESS FORMAT OFFLINE DATA ===');
        try {
          const uniqueCycleNos = [...new Set(processFormatOfflineDataSync.map((d: any) => d.cycleNo))];
          for (const cycleNo of uniqueCycleNos) {
            try {
              const storedStr = localStorage.getItem(`process-format-cycle-${cycleNo}-data`);
              const stored = storedStr ? JSON.parse(storedStr) : null;
              if (stored && Array.isArray(stored) && stored.length > 0) {
                const { savesectionApicall } = await import('../Services/ProcessFormatRecord');
                const result = await savesectionApicall(stored, accessToken);
                if (result.success) {
                  totalSynced += stored.length;
                  localStorage.removeItem(`process-format-cycle-${cycleNo}-data`);
                } else {
                  throw new Error(`Failed to sync Process Format cycle ${cycleNo}: ${result.message}`);
                }
              } else {
                const dataForCycle = processFormatOfflineDataSync.filter((d: any) => d.cycleNo === cycleNo);
                const plantTourIdForSync = planTourState?.plantTourId;
                const userName = user?.Name || 'Current User';
                const { collectProcessFormatRecord, savesectionApicall } = await import('../Services/ProcessFormatRecord');
                const records: any[] = [];
                dataForCycle.forEach((d: any) => {
                  (d.records || []).forEach((r: any) => {
                    (r.items || []).forEach((item: any) => {
                      records.push(
                        collectProcessFormatRecord(
                          cycleNo,
                          r.product || '',
                          r.executiveName || '',
                          r.lineNo || null,
                          item.itemname || null,
                          item.noofbiscuits ? parseInt(String(item.noofbiscuits), 10) : null,
                          item.qtyused ? parseInt(String(item.qtyused), 10) : null,
                          item.status || null,
                          item.variance || null,
                          plantTourIdForSync || 'N/A',
                          userName
                        )
                      );
                    });
                  });
                });
                if (records.length > 0) {
                  const result = await savesectionApicall(records, accessToken);
                  if (result.success) totalSynced += records.length;
                  else throw new Error(`Failed to sync Process Format cycle ${cycleNo}: ${result.message}`);
                }
              }
            } catch (pfError) {
              console.error(`Error syncing Process Format cycle ${cycleNo}:`, pfError);
              totalErrors++;
            }
          }
        } catch (error) {
          console.error('Error syncing Process Format offline data:', error);
          totalErrors++;
        }
      }

      // Sync Proofing offline data
      const proofingOfflineDataSync = store.getState()?.proofing?.offlineSavedData || [];
      if (proofingOfflineDataSync.length > 0) {
        console.log('=== SYNCING PROOFING OFFLINE DATA ===');
        try {
          const uniqueCycleNos = [...new Set(proofingOfflineDataSync.map((d: any) => d.cycleNo))];
          for (const cycleNo of uniqueCycleNos) {
            try {
              const storedStr = localStorage.getItem(`proofing-cycle-${cycleNo}-data`);
              const stored = storedStr ? JSON.parse(storedStr) : null;
              if (stored && Array.isArray(stored) && stored.length > 0) {
                const { savesectionApicall } = await import('../Services/ProofingRecord');
                const result = await savesectionApicall(stored, accessToken);
                if (result.success) {
                  totalSynced += stored.length;
                  localStorage.removeItem(`proofing-cycle-${cycleNo}-data`);
                } else {
                  throw new Error(`Failed to sync Proofing cycle ${cycleNo}: ${result.message}`);
                }
              } else {
                const dataForCycle = proofingOfflineDataSync.filter((d: any) => d.cycleNo === cycleNo);
                const plantTourIdForSync = planTourState?.plantTourId;
                const userName = user?.Name || 'Current User';
                const { collectProofingRecord, savesectionApicall } = await import('../Services/ProofingRecord');
                const records: any[] = [];
                dataForCycle.forEach((d: any) => {
                  (d.records || []).forEach((r: any) => {
                    (r.rows || []).forEach((row: any) => {
                      records.push(
                        collectProofingRecord(
                          cycleNo,
                          r.product || '',
                          r.executiveName || '',
                          r.lineNumber || null,
                          r.observedtime || '',
                          row.variety || null,
                          row.proofingTime || null,
                          row.prooferTemp || null,
                          row.rhProofer || null,
                          plantTourIdForSync || 'N/A',
                          userName
                        )
                      );
                    });
                  });
                });
                if (records.length > 0) {
                  const result = await savesectionApicall(records, accessToken);
                  if (result.success) totalSynced += records.length;
                  else throw new Error(`Failed to sync Proofing cycle ${cycleNo}: ${result.message}`);
                }
              }
            } catch (proofError) {
              console.error(`Error syncing Proofing cycle ${cycleNo}:`, proofError);
              totalErrors++;
            }
          }
        } catch (error) {
          console.error('Error syncing Proofing offline data:', error);
          totalErrors++;
        }
      }

      // Sync Baking Sieves And Magnets offline data
      const bsmOfflineDataSync = store.getState()?.bakingSievesAndMagnets?.offlineSavedData || [];
      if (bsmOfflineDataSync.length > 0) {
        console.log('=== SYNCING BAKING SIEVES AND MAGNETS OFFLINE DATA ===');
        try {
          const uniqueCycleNos = [...new Set(bsmOfflineDataSync.map((d: any) => d.cycleNo))];
          for (const cycleNo of uniqueCycleNos) {
            try {
              const storedStr = localStorage.getItem(`baking-sieves-magnets-cycle-${cycleNo}-data`);
              const stored = storedStr ? JSON.parse(storedStr) : null;
              if (stored && Array.isArray(stored) && stored.length > 0) {
                const { savesectionApicall } = await import('../Services/BakingSievesAndMagnetsRecord');
                const result = await savesectionApicall(stored, accessToken);
                if (result.success) {
                  totalSynced += stored.length;
                  localStorage.removeItem(`baking-sieves-magnets-cycle-${cycleNo}-data`);
                } else {
                  throw new Error(`Failed to sync Baking Sieves And Magnets cycle ${cycleNo}: ${result.message}`);
                }
              } else {
                const dataForCycle = bsmOfflineDataSync.filter((d: any) => d.cycleNo === cycleNo);
                const plantTourIdForSync = planTourState?.plantTourId;
                const userName = user?.Name || 'Current User';
                const { collectBakingSievesAndMagnetsRecord, savesectionApicall } = await import('../Services/BakingSievesAndMagnetsRecord');
                const records: any[] = [];
                dataForCycle.forEach((d: any) => {
                  (d.records || []).forEach((r: any) => {
                    (r.items || []).forEach((item: any) => {
                      records.push(
                        collectBakingSievesAndMagnetsRecord(
                          cycleNo,
                          item.criteria || '',
                          item.title || '',
                          r.timeValue || '',
                          r.executiveName || '',
                          item.status === 'not-okay' ? (item.remarks || 'Defect noted') : null,
                          plantTourIdForSync || 'N/A',
                          userName
                        )
                      );
                    });
                  });
                });
                if (records.length > 0) {
                  const result = await savesectionApicall(records, accessToken);
                  if (result.success) totalSynced += records.length;
                  else throw new Error(`Failed to sync Baking Sieves And Magnets cycle ${cycleNo}: ${result.message}`);
                }
              }
            } catch (bsmError) {
              console.error(`Error syncing Baking Sieves And Magnets cycle ${cycleNo}:`, bsmError);
              totalErrors++;
            }
          }
        } catch (error) {
          console.error('Error syncing Baking Sieves And Magnets offline data:', error);
          totalErrors++;
        }
      }

      // Sync Backing PQI offline data (from backingPQI.offlineQueue / offlineSubmissionsByCategory) using new backingPQIDataverseService
      const backingPQIOfflineByCategory = (store.getState() as { backingPQI?: { offlineSubmissionsByCategory?: Record<string, unknown[]> } })?.backingPQI?.offlineSubmissionsByCategory ?? {};
      const backingPQIOfflineSubmissions = Object.values(backingPQIOfflineByCategory).flat() as { cycleNo: number; records: unknown[] }[];
      if (backingPQIOfflineSubmissions.length > 0) {
        console.log('=== SYNCING BACKING PQI OFFLINE DATA ===');
        try {
          const { saveRecordsToDataverse } = await import('../Services/backingPQIDataverseService');
          for (const submission of backingPQIOfflineSubmissions) {
            const records = submission?.records ?? [];
            if (records.length === 0) continue;
            try {
              const result = await saveRecordsToDataverse(records as Record<string, unknown>[], accessToken);
              if (result.success) totalSynced += records.length;
              else if (result.errors?.length) totalErrors += result.errors.length;
            } catch (err) {
              console.error('Error syncing Backing PQI submission:', err);
              totalErrors++;
            }
          }
        } catch (error) {
          console.error('Error syncing Backing PQI offline data:', error);
          totalErrors++;
        }
      }

      // Show sync results
      if (totalSynced > 0 && totalErrors === 0) {
        alert(`✅ Successfully synced ${totalSynced} item(s)!`);
        
        // Clear Plant Tour Section offline data after successful sync
        if (hasPlantTourOfflineData) {
          const { clearOfflineTourData } = await import('../Services/PlantTourOfflineStorage');
          for (const tourData of plantTourOfflineData) {
            clearOfflineTourData(tourData.plantTourId);
          }
        }
        
        // Clear all offline data from localStorage
        clearAllOfflineData();
        
        // Clear plant tour specific localStorage items
        localStorage.removeItem('plantTourId');
        localStorage.removeItem('offlinePlantTourActive');
        console.log('Plant tour localStorage items cleared');
        
        // Clear offline mode and all related Redux state
        dispatch(setOfflineMode(false));
        dispatch(clearOfflineData()); // Clears offlineCriteriaList, offlineEmployeeDetails, etc.
        dispatch(setOfflineStarted(false));
        dispatch(setOfflineCompleted(false));
        dispatch(resetOfflineState()); // Resets progress and other offline state
        
        // Clear Plant Tour Section specific Redux state
        dispatch(setOfflineCriteriaList([]));
        dispatch(setOfflineEmployeeDetails(null));
        dispatch(setOfflineExistingObservations([]));
        dispatch(setOfflineDataTimestamp(0));
        dispatch(clearBakingAndCoolingOfflineData());
        dispatch(resetBackingCodingState()); // Clear all Backing Coding state including fetchedCycles for fresh start
        dispatch(resetBackingMixingState()); // Clear all Backing Mixing state for fresh start
        dispatch(resetProcessFormatState()); // Clear all Process Format state for fresh start
        dispatch(resetProofingState()); // Clear all Proofing state for fresh start
        dispatch(resetBakingSievesAndMagnetsState()); // Clear all Baking Sieves And Magnets state for fresh start
        dispatch(resetBackingPQIState()); // Clear all Backing PQI state for fresh start
        for (let i = 1; i <= 100; i++) {
          try { localStorage.removeItem(`process-format-cycle-${i}-data`); } catch (_) {}
          try { localStorage.removeItem(`proofing-cycle-${i}-data`); } catch (_) {}
          try { localStorage.removeItem(`baking-sieves-magnets-cycle-${i}-data`); } catch (_) {}
        }
        console.log('All offline data cleared from localStorage and Redux state');
        console.log('Plant Tour Section offline data cleared and offline mode cancelled');
      } else if (totalSynced > 0 && totalErrors > 0) {
        alert(`⚠️ Synced ${totalSynced} item(s) successfully, but ${totalErrors} failed. Please check console for details.`);
      } else if (totalSynced === 0 && totalErrors > 0) {
        alert('❌ Failed to sync offline data. Please check console for details.');
      } else {
        alert('No offline data to sync.');
      }

    } catch (error) {
      console.error('Error during sync/cancel check:', error);
      alert(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleCancelOffline = async () => {
    console.log('Attempting to sync/cancel offline mode...');

    // Check if internet is available
    if (!isOnline) {
      console.log('No internet connection available for syncing');
      alert('⚠️ Internet connection required to sync offline data. Please check your connection and try again.');
      return;
    }

    // Get access token for syncing (forceForSync so we get token even while offline mode is still active)
    const tokenResult = await getAccessToken(undefined, undefined, true);
    const accessToken = tokenResult?.token;

    if (!accessToken) {
      console.error('No access token available for syncing');
      alert('❌ Authentication error. Please log in again.');
      return;
    }

    let totalSynced = 0;
    let totalErrors = 0;

    // Sync Net Weight Evaluation offline data (from offlineSubmissionsByCategory)
    console.log('=== CHECKING NET WEIGHT EVALUATION OFFLINE DATA ===');
    const netWeightEvaluationOfflineData = offlineSubmissionsByCategory['Net Weight Evaluation'] || [];
    console.log('Net Weight Evaluation offline data length:', netWeightEvaluationOfflineData.length);
    console.log('Net Weight Evaluation offline data:', netWeightEvaluationOfflineData);
    
    if (netWeightEvaluationOfflineData.length > 0) {
      console.log('Syncing Net Weight Evaluation offline data...');
      console.log('Net Weight Evaluation pending sync data:', netWeightEvaluationOfflineData);
      try {
        for (const data of netWeightEvaluationOfflineData) {
          try {
            const cycleNo = data.cycleNo;
            const plantTourId = planTourState.plantTourId || data.plantTourId;
            const userName = user?.Name || 'Current User';
            const selectedShift = planTourState.selectedCycle;

            console.log(`Syncing Net Weight Evaluation cycle ${cycleNo}:`, data);
            console.log('Using data for Net Weight Evaluation sync:', {
              cycleNo: data.cycleNo,
              plantTourId,
              userName,
              selectedShift,
              recordsCount: data.records?.length || 0,
              packsCount: data.packs?.length || 0
            });

            // Import the sync function from saveSectionData service
            console.log('Importing syncOfflineDataToBackend function from saveSectionData...');
            const { syncOfflineDataToBackend } = await import('../Services/saveSectionData');
            console.log('syncOfflineDataToBackend function imported successfully');
            
            // Sync the offline data
            console.log('Calling syncOfflineDataToBackend with data:', [data]);
            const response = await syncOfflineDataToBackend(
              [data], // Pass as array since the function expects NetWeightEvaluationOfflineSubmission[]
              plantTourId || 'N/A',
              userName,
              selectedShift
            );
            console.log('syncOfflineDataToBackend response:', response);

            if (response.success) {
              console.log(`Successfully synced Net Weight Evaluation cycle ${cycleNo}`);
              totalSynced++;
            } else {
              throw new Error(`Failed to sync Net Weight Evaluation cycle ${cycleNo}: ${response.message}`);
            }
          } catch (nweError) {
            console.error(`Failed to sync Net Weight Evaluation cycle ${data.cycleNo}:`, nweError);
            totalErrors++;
          }
        }

        console.log('Net Weight Evaluation offline data sync completed');
      } catch (error) {
        console.error('Error syncing Net Weight Evaluation offline data:', error);
        totalErrors++;
      }
    } else {
      console.log('No Net Weight Evaluation offline data to sync');
    }

    // Sync Quality Wall Record offline data (from offlineSubmissionsByCategory)
    console.log('=== CHECKING QUALITY WALL RECORD OFFLINE DATA ===');
    const qualityWallRecordOfflineData = offlineSubmissionsByCategory['Quality Wall Record'] || [];
    console.log('Quality Wall Record offline data length:', qualityWallRecordOfflineData.length);
    console.log('Quality Wall Record offline data:', qualityWallRecordOfflineData);
    
    if (qualityWallRecordOfflineData.length > 0) {
      console.log('Syncing Quality Wall Record offline data...');
      console.log('Quality Wall Record pending sync data:', qualityWallRecordOfflineData);
      try {
        for (const data of qualityWallRecordOfflineData) {
          try {
            const cycleNo = data.cycleNo;
            const plantTourId = planTourState.plantTourId || data.plantTourId;
            const userName = user?.Name || 'Current User';
            const selectedShift = planTourState.selectedCycle;

            console.log(`Syncing Quality Wall Record cycle ${cycleNo}:`, data);
            console.log('Using data for Quality Wall Record sync:', {
              cycleNo: data.cycleNo,
              plantTourId,
              userName,
              selectedShift,
              recordsCount: data.records?.length || 0
            });

            const { syncOfflineDataToBackend } = await import('../Services/QualityWallRecord');
            const response = await syncOfflineDataToBackend(
              [data],
              plantTourId || 'N/A',
              userName,
              selectedShift
            );

            if (response.success) {
              console.log(`Successfully synced Quality Wall Record cycle ${cycleNo}`);
              totalSynced++;
            } else {
              throw new Error(`Failed to sync Quality Wall Record cycle ${cycleNo}: ${response.message}`);
            }
          } catch (qwrError) {
            console.error(`Failed to sync Quality Wall Record cycle ${data.cycleNo}:`, qwrError);
            totalErrors++;
          }
        }
        console.log('Quality Wall Record offline data sync completed');
      } catch (error) {
        console.error('Error syncing Quality Wall Record offline data:', error);
        totalErrors++;
      }
    } else {
      console.log('No Quality Wall Record offline data to sync');
    }

    // Sync Process Parameter Verification offline data (from offlineSubmissionsByCategory)
    console.log('=== CHECKING PROCESS PARAMETER VERIFICATION OFFLINE DATA ===');
    const processParameterVerificationOfflineData = offlineSubmissionsByCategory['Process Parameter Verification'] || [];
    console.log('Process Parameter Verification offline data length:', processParameterVerificationOfflineData.length);
    console.log('Process Parameter Verification offline data:', processParameterVerificationOfflineData);
    
    if (processParameterVerificationOfflineData.length > 0) {
      console.log('Syncing Process Parameter Verification offline data...');
      console.log('Process Parameter Verification pending sync data:', processParameterVerificationOfflineData);
      try {
        for (const data of processParameterVerificationOfflineData) {
          try {
            const cycleNo = data.cycleNo;
            const plantTourId = planTourState.plantTourId || data.plantTourId;
            const userName = user?.Name || 'Current User';
            const selectedShift = planTourState.selectedCycle;

            console.log(`Syncing Process Parameter Verification cycle ${cycleNo}:`, data);
            console.log('Using data for Process Parameter Verification sync:', {
              cycleNo: data.cycleNo,
              plantTourId,
              userName,
              selectedShift,
              recordsCount: data.records?.length || 0
            });

            const { syncOfflineDataToBackend } = await import('../Services/MixingParameterVerification.ts');
            const response = await syncOfflineDataToBackend(
              [data],
              plantTourId || 'N/A',
              userName,
              selectedShift
            );

            if (response.success) {
              console.log(`Successfully synced Process Parameter Verification cycle ${cycleNo}`);
              totalSynced++;
            } else {
              throw new Error(`Failed to sync Process Parameter Verification cycle ${cycleNo}: ${response.message}`);
            }
          } catch (ppvError) {
            console.error(`Failed to sync Process Parameter Verification cycle ${data.cycleNo}:`, ppvError);
            totalErrors++;
          }
        }
        console.log('Process Parameter Verification offline data sync completed');
      } catch (error) {
        console.error('Error syncing Process Parameter Verification offline data:', error);
        totalErrors++;
      }
    } else {
      console.log('No Process Parameter Verification offline data to sync');
    }

    // Sync general offline submissions (excluding Net Weight Evaluation, Quality Wall Record, and Process Parameter Verification which are handled separately)
    const categoriesToExclude = ['Net Weight Evaluation', 'Quality Wall Record', 'Process Parameter Verification'];
    const filteredCategories = Object.entries(offlineSubmissionsByCategory || {})
      .filter(([category]) => !categoriesToExclude.includes(category));
    const allOfflineSubmissions = filteredCategories.flatMap(([, submissions]) => submissions);
    console.log('HomePage: All offline submissions by category (excluding Net Weight Evaluation):', offlineSubmissionsByCategory);
    console.log('HomePage: Flattened offline submissions (excluding Net Weight Evaluation):', allOfflineSubmissions);

    if (allOfflineSubmissions.length > 0) {
      console.log('Syncing general offline submissions...');
      try {
        // Remove duplicates before syncing
        const uniqueSubmissions = removeDuplicateSubmissions(allOfflineSubmissions);
        console.log(`Original submissions: ${allOfflineSubmissions.length}, Unique submissions: ${uniqueSubmissions.length}`);
        console.log('HomePage: Unique submissions to sync:', uniqueSubmissions);

        for (const submission of uniqueSubmissions) {
          console.log(`Processing submission for cycle ${submission.cycleNo}:`, {
            recordsCount: submission.records.length,
            records: submission.records.map((r: any) => ({
              evaluationType: r.cr3ea_evaluationtype,
              cycle: r.cr3ea_cycle,
              criteria: r.cr3ea_criteria
            }))
          });

          // Validate records before syncing
          if (!submission.records || submission.records.length === 0) {
            console.warn(`Skipping empty submission for cycle ${submission.cycleNo}`);
            continue;
          }

          const result = await saveSectionData(accessToken, submission.records);
          console.log(`Synced submission for cycle ${submission.cycleNo}, result:`, result);
          totalSynced++;
        }
        console.log('All general offline submissions synced successfully');
      } catch (error) {
        console.error('Error syncing general offline submissions:', error);
        totalErrors++;
      }
    } else {
      console.log('No general offline submissions to sync');
    }

    // Sync cream percentage offline data
    if (creamPercentagePendingSync.length > 0) {
      console.log('=== SYNCING CREAM PERCENTAGE OFFLINE DATA ===');
      console.log('Total items to sync:', creamPercentagePendingSync.length);
      console.log('Cream percentage pending sync data:', creamPercentagePendingSync);
      console.log('Items detail:', creamPercentagePendingSync.map((p: any, i: number) => ({
        index: i,
        cycleNum: p.cycleNum,
        product: p.formData?.product,
        timestamp: p.timestamp
      })));

      try {
        let syncIndex = 0;
        for (const data of creamPercentagePendingSync) {
          console.log(`--- Syncing item ${syncIndex + 1}/${creamPercentagePendingSync.length} ---`);
          console.log(`Cream percentage cycle ${data.cycleNum}, product: ${data.formData?.product}`);
          console.log('Full data:', data);

          try {
            await saveCreamPercentageData({
              cycleNum: data.cycleNum,
              formData: data.formData,
              weightData: data.weightData,
              qualityTourId: data.qualityTourId,
              userName: data.userName,
              shiftValue: data.shiftValue
            });

            console.log(`Successfully synced cream percentage item ${syncIndex + 1}: cycle ${data.cycleNum}, product: ${data.formData?.product}`);
            totalSynced++;
          } catch (cycleError) {
            console.error(`Failed to sync cream percentage item ${syncIndex + 1}: cycle ${data.cycleNum}:`, cycleError);
            totalErrors++;
          }
          syncIndex++;
        }

        console.log('=== CREAM PERCENTAGE SYNC COMPLETED ===');
        console.log(`Total synced: ${syncIndex}, Success: ${totalSynced}, Errors: ${totalErrors}`);
      } catch (error) {
        console.error('Error syncing cream percentage offline data:', error);
        totalErrors++;
      }
    } else {
      console.log('No cream percentage offline data to sync');
    }

    // Sync Sieve and Magnet New Plant offline data
    if (sieveAndMagnetNewPlantPendingSync.length > 0) {
      console.log('Syncing Sieve and Magnet New Plant offline data...');
      console.log('Sieve and Magnet New Plant pending sync data:', sieveAndMagnetNewPlantPendingSync);

      try {
        for (const data of sieveAndMagnetNewPlantPendingSync) {
          console.log(`Syncing Sieve and Magnet New Plant cycle ${data.cycleNumber}:`, data);

          try {
            // Import the required functions
            const { saveSieveAndMagnetNewPlant, collectEstimationDataCycleSave } = await import('../Services/saveSieveAndMagnetNewPlant');
            
            // Get the required data from Redux state
            const plantTourId = planTourState.plantTourId;
            const selectedCycle = planTourState.selectedCycle || 'Shift 1';
            const userName = user?.Name || 'Current User';
            
            console.log('Using data for sync:', {
              cycleNumber: data.cycleNumber,
              plantTourId,
              selectedCycle,
              userName,
              checklistItemsCount: data.checklistItems.length
            });
            
            // Ensure start-data is available for the cycle (fallback to pending formData)
            try {
              if (data.formData && (data.formData.product || data.formData.executiveName)) {
                localStorage.setItem(`cycle-${data.cycleNumber}-start-data`, JSON.stringify({ product: data.formData.product || 'N/A', executiveName: data.formData.executiveName || 'N/A' }));
                console.log('Applied pending formData to localStorage start-data for cycle', data.cycleNumber);
              }
            } catch (e) {}

            const { savedData } = collectEstimationDataCycleSave(
              data.cycleNumber,
              data.checklistItems,
              plantTourId || 'N/A',
              selectedCycle,
              userName
            );
            console.log('Generated savedData for API:', savedData);

            const response = await saveSieveAndMagnetNewPlant(savedData);
            
            if (response.success) {
              console.log(`Successfully synced Sieve and Magnet New Plant cycle ${data.cycleNumber}`);
              totalSynced++;
            } else {
              throw new Error(`Failed to sync cycle ${data.cycleNumber}: ${response.message}`);
            }
          } catch (cycleError) {
            console.error(`Failed to sync Sieve and Magnet New Plant cycle ${data.cycleNumber}:`, cycleError);
            totalErrors++;
          }
        }

        console.log('Sieve and Magnet New Plant offline data sync completed');
      } catch (error) {
        console.error('Error syncing Sieve and Magnet New Plant offline data:', error);
        totalErrors++;
      }
    } else {
      console.log('No Sieve and Magnet New Plant offline data to sync');
    }

    // Sync Sieve and Magnet Old Plant offline data
    if (sieveAndMagnetOldPlantPendingSync.length > 0) {
      console.log('Syncing Sieve and Magnet Old Plant offline data...');
      console.log('Sieve and Magnet Old Plant pending sync data:', sieveAndMagnetOldPlantPendingSync);

      try {
        for (const data of sieveAndMagnetOldPlantPendingSync) {
          console.log(`Syncing Sieve and Magnet Old Plant cycle ${data.cycleNumber}:`, data);

          try {
            // Import the required functions
            const { saveSieveAndMagnetOldPlant, collectEstimationDataCycleSave } = await import('../Services/saveSieveAndMagnetOldPlant');
            
            // Get the required data from Redux state
            const plantTourId = planTourState.plantTourId;
            const selectedCycle = planTourState.selectedCycle || 'Shift 1';
            const userName = user?.Name || 'Current User';
            
            console.log('Using data for sync:', {
              cycleNumber: data.cycleNumber,
              plantTourId,
              selectedCycle,
              userName,
              checklistItemsCount: data.checklistItems.length
            });
            
            // Ensure start-data is available for the cycle (fallback to pending formData)
            try {
              if (data.formData && (data.formData.product || data.formData.executiveName)) {
                localStorage.setItem(`cycle-${data.cycleNumber}-start-data`, JSON.stringify({ product: data.formData.product || 'N/A', executiveName: data.formData.executiveName || 'N/A' }));
                console.log('Applied pending formData to localStorage start-data for cycle', data.cycleNumber);
              }
            } catch (e) {}

            const { savedData } = collectEstimationDataCycleSave(
              data.cycleNumber,
              data.checklistItems,
              plantTourId || 'N/A',
              selectedCycle,
              userName
            );



            const response = await saveSieveAndMagnetOldPlant(savedData);
            
            if (response.success) {
              console.log(`Successfully synced Sieve and Magnet Old Plant cycle ${data.cycleNumber}`);
              totalSynced++;
            } else {
              throw new Error(`Failed to sync cycle ${data.cycleNumber}: ${response.message}`);
            }
          } catch (cycleError) {
            console.error(`Failed to sync Sieve and Magnet Old Plant cycle ${data.cycleNumber}:`, cycleError);
            totalErrors++;
          }
        }

        console.log('Sieve and Magnet Old Plant offline data sync completed');
      } catch (error) {
        console.error('Error syncing Sieve and Magnet Old Plant offline data:', error);
        totalErrors++;
      }
    } else {
      console.log('No Sieve and Magnet Old Plant offline data to sync');
    }

    // Sync Product Monitoring offline data
    if (productMonitoringPendingSync.length > 0) {
      console.log('Syncing Product Monitoring offline data...');
      console.log('Product Monitoring pending sync data:', productMonitoringPendingSync);

      try {
        for (const data of productMonitoringPendingSync) {
          console.log(`Syncing Product Monitoring cycle ${data.cycleNumber}:`, data);

          try {
            // Import the required functions
            const { saveProductMonitoringData, collectEstimationDataCycleSave } = await import('../Services/productMonitoringRecord');
            
            // Get the required data from Redux state
            const plantTourId = planTourState.plantTourId;
            const selectedCycle = planTourState.selectedCycle || 'Shift 1';
            const userName = user?.Name || 'Current User';
            
            console.log('Using data for Product Monitoring sync:', {
              cycleNumber: data.cycleNumber,
              plantTourId,
              selectedCycle,
              userName,
              cycleData: data.cycleData
            });
            
            const { savedData } = collectEstimationDataCycleSave(
              data.cycleNumber,
              {
                product: data.cycleData.product,
                moisture: data.cycleData.cr3ea_moisture,
                gaugeOperating: data.cycleData.cr3ea_gaugeoperating,
                gaugeNonOperating: data.cycleData.cr3ea_gaugenonoperating,
                gaugeCentre: data.cycleData.cr3ea_gaugecentre,
                dryWeightOvenEndOperating: data.cycleData.cr3ea_dryweightovenendoperating,
                dryWeightOvenEndNonOperating: data.cycleData.cr3ea_dryweightovenendnonoperating,
                dryWeightOvenEndCentre: data.cycleData.cr3ea_dryweightovenendcentre,
                dimensionOperating: data.cycleData.cr3ea_dimensionoperating,
                dimensionNonOperating: data.cycleData.cr3ea_dimensionnonoperating,
                dimensionCentre: data.cycleData.cr3ea_dimensioncentre,
              },
              plantTourId || 'N/A',
              selectedCycle,
              userName
            );

            const response = await saveProductMonitoringData(savedData);
            
            if (response.success) {
              console.log(`Successfully synced Product Monitoring cycle ${data.cycleNumber}`);
              totalSynced++;
            } else {
              throw new Error(`Failed to sync cycle ${data.cycleNumber}: ${response.message}`);
            }
          } catch (cycleError) {
            console.error(`Failed to sync Product Monitoring cycle ${data.cycleNumber}:`, cycleError);
            totalErrors++;
          }
        }

        console.log('Product Monitoring offline data sync completed');
      } catch (error) {
        console.error('Error syncing Product Monitoring offline data:', error);
        totalErrors++;
      }
    } else {
      console.log('No Product Monitoring offline data to sync');
    }

    // Sync Seal Integrity Test offline data
    if (sealIntegrityOfflineData.length > 0) {
      console.log('Syncing Seal Integrity Test offline data...');
      console.log('Seal Integrity Test pending sync data:', sealIntegrityOfflineData);
      try {
        for (const data of sealIntegrityOfflineData) {
          try {
            const cycleNo = data.cycleNo;
            const plantTourId = planTourState.plantTourId;
            const userName = user?.Name || 'Current User';

            // Build payload from DOM if available, else from localStorage snapshot saved during offline save
            const storedStr = localStorage.getItem(`sit-cycle-${cycleNo}-values`);
            const stored = storedStr ? JSON.parse(storedStr) : null;

            const payload = [{
              cr3ea_qualitytourid: plantTourId || 'N/A',
              cr3ea_title: `SealIntegrityTest_${(() => {
                const now = new Date();
                const day = String(now.getDate()).padStart(2, '0');
                const month = String(now.getMonth() + 1).padStart(2, '0');
                const year = now.getFullYear();
                return `${day}/${month}/${year}`;
              })()}`,
              cr3ea_cycle: `Cycle-${cycleNo}`,
              cr3ea_shift: sessionStorage.getItem('shiftValue') || 'shift 1',
              cr3ea_tourstartdate: (() => {
                const now = new Date();
                const day = String(now.getDate()).padStart(2, '0');
                const month = String(now.getMonth() + 1).padStart(2, '0');
                const year = now.getFullYear();
                return `${day}/${month}/${year}`;
              })(),
              cr3ea_observedtime: stored?.observedtime || '',
              cr3ea_observedby: userName,
              cr3ea_productname: stored?.product || 'N/A',
              cr3ea_machineno: stored?.machineNo || '',
              cr3ea_sampleqty: stored?.sampleqty || '',
              cr3ea_leakageno: stored?.leakageno || '',
              cr3ea_leakagetype: stored?.leakagetype || '',
              cr3ea_executivename: stored?.executiveName || 'N/A',
              cr3ea_platform: 'PWA',
            }];

            const response = await saveSealSection(payload as any);
            if (response.success) {
              console.log(`Successfully synced Seal Integrity Test cycle ${cycleNo}`);
              totalSynced++;
              try { localStorage.removeItem(`sit-cycle-${cycleNo}-values`); } catch {}
            } else {
              throw new Error(`Failed to sync Seal Integrity Test cycle ${cycleNo}: ${response.message}`);
            }
          } catch (sitError) {
            console.error('Error syncing Seal Integrity Test item:', sitError);
            totalErrors++;
          }
        }
        console.log('Seal Integrity Test offline data sync completed');
      } catch (error) {
        console.error('Error syncing Seal Integrity Test offline data:', error);
        totalErrors++;
      }
    } else {
      console.log('No Seal Integrity Test offline data to sync');
    }

    // Sync ALC offline data
    if (alcOfflineData.length > 0) {
      console.log('Syncing ALC offline data...');
      try {
        for (const data of alcOfflineData) {
          try {
            const cycleNo = data.cycleNo;
            const cached = localStorage.getItem(`alc-payload-cycle-${cycleNo}`);
            if (cached) {
              const payload = JSON.parse(cached);
              const { saveSectionApiCall } = await import('../Services/ALC');
              const response = await saveSectionApiCall(payload);
              if (response.success) {
                console.log(`Successfully synced ALC cycle ${cycleNo}`);
                totalSynced++;
                try { localStorage.removeItem(`alc-payload-cycle-${cycleNo}`); } catch {}
              } else {
                throw new Error(`Failed to sync ALC cycle ${cycleNo}: ${response.message}`);
              }
            } else {
              console.warn(`No cached ALC payload found for cycle ${cycleNo}`);
            }
          } catch (alcErr) {
            console.error('Error syncing ALC item:', alcErr);
            totalErrors++;
          }
        }
      } catch (error) {
        console.error('Error syncing ALC offline data:', error);
        totalErrors++;
      }
    } else {
      console.log('No ALC offline data to sync');
    }

    // Sync Code Verification offline data
    if (codeVerificationOfflineData.length > 0) {
      console.log('Syncing Code Verification offline data...');
      console.log('Code Verification pending sync data:', codeVerificationOfflineData);
      console.log('Code Verification offline data count:', codeVerificationOfflineData.length);

      try {
        for (const data of codeVerificationOfflineData) {
          console.log(`Syncing Code Verification cycle ${data.cycleNo}:`, data);

          try {
            // Import the required functions
            const { saveSectionApiCall, collectEstimationDataCycleSave } = await import('../Services/CodeVerificationRecord');
            
            // Get the required data from Redux state
            const plantTourId = planTourState.plantTourId;
            const userName = user?.Name || 'Current User';
            
            console.log('Using data for Code Verification sync:', {
              cycleNo: data.cycleNo,
              plantTourId,
              userName,
              recordsCount: data.records.length
            });
            
            const apiRecords = data.records.map((record: any) => ({
              sku: record.sku,
              majorDefectsRemarks: record.remarks,
              pkdDate: record.pkdDate || '',
              expiryDate: record.expiryDate || '',
              batchNo: record.batchNo || '',
              attachments: record.attachments ?? '',
            }));

            const { savedData } = await collectEstimationDataCycleSave(
              data.cycleNo,
              apiRecords[0], // Use the first record as the main form data
              plantTourId || 'N/A',
              userName
            );

            const response = await saveSectionApiCall(savedData);
            
            if (response.success) {
              console.log(`Successfully synced Code Verification cycle ${data.cycleNo}`);
              totalSynced++;
            } else {
              throw new Error(`Failed to sync cycle ${data.cycleNo}: ${response.message}`);
            }
          } catch (cycleError) {
            console.error(`Failed to sync Code Verification cycle ${data.cycleNo}:`, cycleError);
            totalErrors++;
          }
        }

        console.log('Code Verification offline data sync completed');
      } catch (error) {
        console.error('Error syncing Code Verification offline data:', error);
        totalErrors++;
      }
    } else {
      console.log('No Code Verification offline data to sync');
    }

    // Sync Code Verification offline files (SharePoint REST needs MSAL token for bectors.sharepoint.com — not getAccessToken / Dataverse token)
    try {
      const { syncOfflineFilesToSharePoint } = await import('../Services/CodeVerificationFileUpload');
      if (!accounts[0]) {
        console.warn('Skipping Code Verification file sync: no signed-in MSAL account');
      } else {
        const spResponse = await instance.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
        });
        const accessToken = spResponse.accessToken;
        if (accessToken) {
          console.log('Syncing Code Verification offline files...');
          const fileSyncResult = await syncOfflineFilesToSharePoint(accessToken);
          console.log('Code Verification file sync result:', fileSyncResult);
          if (fileSyncResult.syncedCount > 0) {
            console.log(`Successfully synced ${fileSyncResult.syncedCount} Code Verification file(s)`);
          }
        }
      }
    } catch (fileError) {
      console.error('Error syncing Code Verification offline files:', fileError);
    }

    // Sync Baking Process offline data
    if (bakingOfflineData.length > 0) {
      console.log('Syncing Baking Process offline data...');
      console.log('Baking Process pending sync data:', bakingOfflineData);
      try {
        for (const data of bakingOfflineData) {
          try {
            const cycleNo = data.cycleNo;
            const plantTourId = planTourState.plantTourId;
            const userName = user?.Name || 'Current User';

            // Try to collect from DOM (likely empty on Home) and then merge with stored values from localStorage
            const storedStr = localStorage.getItem(`baking-cycle-${cycleNo}-values`);
            const stored = storedStr ? JSON.parse(storedStr) : null;

            const prefer = (a?: any, b?: any) => {
              if (a !== undefined && a !== null && a !== '') return a;
              if (b !== undefined && b !== null && b !== '') return b;
              return '';
            };

            let mergedPayload: any[] | null = null;
            try {
              const { savedData } = await collectBakingForSync(cycleNo, plantTourId || 'N/A', userName);
              const collected = Array.isArray(savedData) && savedData.length > 0 ? savedData[0] : null;
              if (collected || stored) {
                const merged = {
                  cr3ea_qualitytourid: plantTourId || 'N/A',
                  cr3ea_title: collected?.cr3ea_title || 'Baking_ManualSync',
                  cr3ea_cycle: `Cycle-${cycleNo}`,
                  cr3ea_shift: collected?.cr3ea_shift || sessionStorage.getItem('shiftValue') || 'shift 1',
                  cr3ea_tourstartdate: collected?.cr3ea_tourstartdate || (() => {
                    const now = new Date();
                    const day = String(now.getDate()).padStart(2, '0');
                    const month = String(now.getMonth() + 1).padStart(2, '0');
                    const year = now.getFullYear();
                    return `${day}/${month}/${year}`;
                  })(),
                  cr3ea_observedby: collected?.cr3ea_observedby || userName,
                  cr3ea_productname: prefer(collected?.cr3ea_productname, stored?.product) || 'N/A',
                  cr3ea_bakingtime: prefer(collected?.cr3ea_bakingtime, stored?.bakingTime) || 'N/A',
                  cr3ea_topbakingtempzone1: prefer(collected?.cr3ea_topbakingtempzone1, stored?.topZones?.zone1),
                  cr3ea_topbakingtempzone2: prefer(collected?.cr3ea_topbakingtempzone2, stored?.topZones?.zone2),
                  cr3ea_topbakingtempzone3: prefer(collected?.cr3ea_topbakingtempzone3, stored?.topZones?.zone3),
                  cr3ea_topbakingtempzone4: prefer(collected?.cr3ea_topbakingtempzone4, stored?.topZones?.zone4),
                  cr3ea_topbakingtempzone5: prefer(collected?.cr3ea_topbakingtempzone5, stored?.topZones?.zone5),
                  cr3ea_topbakingtempzone6: prefer(collected?.cr3ea_topbakingtempzone6, stored?.topZones?.zone6),
                  cr3ea_topbakingtempzone7: prefer(collected?.cr3ea_topbakingtempzone7, stored?.topZones?.zone7),
                  cr3ea_topproducttempafterbaking: prefer(collected?.cr3ea_topproducttempafterbaking, stored?.topZones?.productTempAfter),
                  cr3ea_bottombakingtempzone1: prefer(collected?.cr3ea_bottombakingtempzone1, stored?.bottomZones?.zone1),
                  cr3ea_bottombakingtempzone2: prefer(collected?.cr3ea_bottombakingtempzone2, stored?.bottomZones?.zone2),
                  cr3ea_bottombakingtempzone3: prefer(collected?.cr3ea_bottombakingtempzone3, stored?.bottomZones?.zone3),
                  cr3ea_bottombakingtempzone4: prefer(collected?.cr3ea_bottombakingtempzone4, stored?.bottomZones?.zone4),
                  cr3ea_bottombakingtempzone5: prefer(collected?.cr3ea_bottombakingtempzone5, stored?.bottomZones?.zone5),
                  cr3ea_bottombakingtempzone6: prefer(collected?.cr3ea_bottombakingtempzone6, stored?.bottomZones?.zone6),
                  cr3ea_bottombakingtempzone7: prefer(collected?.cr3ea_bottombakingtempzone7, stored?.bottomZones?.zone7),
                  cr3ea_bottomproducttempafterbaking: prefer(collected?.cr3ea_bottomproducttempafterbaking, stored?.bottomZones?.productTempAfter),
                  cr3ea_executivename: prefer(collected?.cr3ea_executivename, stored?.executiveName) || 'N/A',
                  cr3ea_platform: 'PWA',
                };
                mergedPayload = [merged];
              }
            } catch (collectErr) {
              console.warn('collectBakingForSync errored on HomePage. Falling back to stored only.', collectErr);
              if (stored) {
                mergedPayload = [{
                  cr3ea_qualitytourid: plantTourId || 'N/A',
                  cr3ea_title: 'Baking_ManualSync',
                  cr3ea_cycle: `Cycle-${cycleNo}`,
                  cr3ea_shift: sessionStorage.getItem('shiftValue') || 'shift 1',
                  cr3ea_tourstartdate: (() => {
                    const now = new Date();
                    const day = String(now.getDate()).padStart(2, '0');
                    const month = String(now.getMonth() + 1).padStart(2, '0');
                    const year = now.getFullYear();
                    return `${day}/${month}/${year}`;
                  })(),
                  cr3ea_observedby: userName,
                  cr3ea_productname: stored.product || 'N/A',
                  cr3ea_bakingtime: stored.bakingTime || 'N/A',
                  cr3ea_topbakingtempzone1: stored.topZones?.zone1 || '',
                  cr3ea_topbakingtempzone2: stored.topZones?.zone2 || '',
                  cr3ea_topbakingtempzone3: stored.topZones?.zone3 || '',
                  cr3ea_topbakingtempzone4: stored.topZones?.zone4 || '',
                  cr3ea_topbakingtempzone5: stored.topZones?.zone5 || '',
                  cr3ea_topbakingtempzone6: stored.topZones?.zone6 || '',
                  cr3ea_topbakingtempzone7: stored.topZones?.zone7 || '',
                  cr3ea_topproducttempafterbaking: stored.topZones?.productTempAfter || '',
                  cr3ea_bottombakingtempzone1: stored.bottomZones?.zone1 || '',
                  cr3ea_bottombakingtempzone2: stored.bottomZones?.zone2 || '',
                  cr3ea_bottombakingtempzone3: stored.bottomZones?.zone3 || '',
                  cr3ea_bottombakingtempzone4: stored.bottomZones?.zone4 || '',
                  cr3ea_bottombakingtempzone5: stored.bottomZones?.zone5 || '',
                  cr3ea_bottombakingtempzone6: stored.bottomZones?.zone6 || '',
                  cr3ea_bottombakingtempzone7: stored.bottomZones?.zone7 || '',
                  cr3ea_bottomproducttempafterbaking: stored.bottomZones?.productTempAfter || '',
                  cr3ea_executivename: stored.executiveName || 'N/A',
                  cr3ea_platform: 'PWA',
                }];
              }
            }

            if (mergedPayload && mergedPayload.length > 0) {
              const response = await saveBakingSection(mergedPayload as any);
              if (response.success) {
                console.log(`Successfully synced Baking Process cycle ${cycleNo}`);
                totalSynced++;
                // Clear the localStorage marker for this cycle
                try { localStorage.removeItem(`baking-cycle-${cycleNo}-values`); } catch {}
              } else {
                throw new Error(`Failed to sync Baking Process cycle ${cycleNo}: ${response.message}`);
              }
            } else {
              console.warn(`No savedData payload built for Baking Process cycle ${cycleNo}. Skipping.`);
            }
          } catch (bpError) {
            console.error('Error syncing Baking Process item:', bpError);
            totalErrors++;
          }
        }
        console.log('Baking Process offline data sync completed');
      } catch (error) {
        console.error('Error syncing Baking Process offline data:', error);
        totalErrors++;
      }
    } else {
      console.log('No Baking Process offline data to sync');
    }

    // Sync Baking Process offline images
    try {
      const state = store.getState();
      const bakingOfflineFiles = state.bakingProcess.offlineFiles;
      if (bakingOfflineFiles.length > 0) {
        console.log('=== SYNCING BAKING PROCESS OFFLINE IMAGES ===');
        console.log('Baking Process offline images found:', bakingOfflineFiles.length);
        
        const accessToken = await getMsalToken(instance, accounts);
        if (accessToken) {
          const imageSyncResult = await syncOfflineFiles(accessToken);
          if (imageSyncResult.success) {
            console.log('Successfully synced baking process images');
            totalSynced += bakingOfflineFiles.length;
          } else {
            console.error('Failed to sync baking process images:', imageSyncResult);
            totalErrors += bakingOfflineFiles.length;
          }
        } else {
          console.error('No access token available for baking process image sync');
          totalErrors += bakingOfflineFiles.length;
        }
      }
    } catch (error) {
      console.error('Error syncing baking process offline images:', error);
      totalErrors++;
    }

    // Sync Backing PQI offline data (Quality - Rajpura main flow)
    const backingPQIState = (store.getState() as { backingPQI?: { offlineQueue?: { records: unknown[] }[]; offlineSubmissionsByCategory?: Record<string, { cycleNo: number; records: unknown[] }[]> } })?.backingPQI;
    const backingPQIOfflineByCategory = backingPQIState?.offlineSubmissionsByCategory ?? {};
    const backingPQIOfflineSubmissions = Object.values(backingPQIOfflineByCategory).flat() as { cycleNo: number; records: unknown[] }[];
    if (backingPQIOfflineSubmissions.length > 0) {
      console.log('=== SYNCING BACKING PQI OFFLINE DATA (handleCancelOffline) ===');
      try {
        const { saveRecordsToDataverse } = await import('../Services/backingPQIDataverseService');
        for (const submission of backingPQIOfflineSubmissions) {
          const records = submission?.records ?? [];
          if (records.length === 0) continue;
          try {
            const result = await saveRecordsToDataverse(records as Record<string, unknown>[], accessToken);
            if (result.success) totalSynced += records.length;
            else if (result.errors?.length) totalErrors += result.errors.length;
          } catch (err) {
            console.error('Error syncing Backing PQI submission:', err);
            totalErrors++;
          }
        }
      } catch (error) {
        console.error('Error syncing Backing PQI offline data:', error);
        totalErrors++;
      }
    }

    // Sync Baking and Cooling offline data
    console.log('=== CHECKING BAKING AND COOLING OFFLINE DATA ===');
    console.log('Baking and Cooling offline data length:', bakingAndCoolingOfflineData.length);
    console.log('Baking and Cooling offline data:', bakingAndCoolingOfflineData);
    
    if (bakingAndCoolingOfflineData.length > 0) {
      console.log('Syncing Baking and Cooling offline data...');
      try {
        for (const data of bakingAndCoolingOfflineData) {
          try {
            const cycleNo = data.cycleNo;
            const plantTourId = planTourState.plantTourId;
            const userName = user?.Name || 'Current User';

            console.log(`Syncing Baking and Cooling cycle ${cycleNo}:`, data);

            // Get stored data from localStorage
            const storedStr = localStorage.getItem(`baking-cooling-cycle-${cycleNo}-data`);
            const stored = storedStr ? JSON.parse(storedStr) : null;

            if (stored && Array.isArray(stored) && stored.length > 0) {
              // Use stored data directly
              const { savesectionApicall } = await import('../Services/BakingAndCoolingRecord');
              const result = await savesectionApicall(stored, accessToken);
              
              if (result.success) {
                console.log(`Successfully synced Baking and Cooling cycle ${cycleNo}`);
                totalSynced++;
                // Remove from localStorage after successful sync
                localStorage.removeItem(`baking-cooling-cycle-${cycleNo}-data`);
              } else {
                throw new Error(`Failed to sync Baking and Cooling cycle ${cycleNo}: ${result.message}`);
              }
            } else {
              // Try to reconstruct from Redux data
              if (data.records && data.records.length > 0) {
                const record = data.records[0];
                const { collectEstimationDataCycleSave, savesectionApicall } = await import('../Services/BakingAndCoolingRecord');
                
                const { savedData } = await collectEstimationDataCycleSave(
                  cycleNo,
                  {
                    productName: record.product || '',
                    lineNumber: record.lineNumber || '',
                    executiveName: record.executiveName || '',
                    bakingTime: record.bakingTime || ''
                  },
                  {
                    bakingTempZone1: record.bakingTempZone1 || '',
                    bakingTempZone2: record.bakingTempZone2 || '',
                    bakingTempZone3: record.bakingTempZone3 || '',
                    bakingTempZone4: record.bakingTempZone4 || '',
                    productTempAfterBaking: record.productTempAfterBaking || '',
                    ap: record.ap || '',
                    std: record.std || '',
                    ad: record.ad || '',
                    ovenFireStartTime: record.ovenFireStartTime || '',
                    ovenStopTime: record.ovenStopTime || '',
                    productInTime: record.productInTime || '',
                    productOutTime: record.productOutTime || '',
                    coolingTime: record.coolingTime || '',
                    coolingTemp: record.coolingTemp || '',
                    coreTemp: record.coreTemp || '',
                    productColor: record.productColor || ''
                  },
                  plantTourId || 'N/A',
                  userName
                );
                
                const result = await savesectionApicall(savedData, accessToken);
                
                if (result.success) {
                  console.log(`Successfully synced Baking and Cooling cycle ${cycleNo}`);
                  totalSynced++;
                } else {
                  throw new Error(`Failed to sync Baking and Cooling cycle ${cycleNo}: ${result.message}`);
                }
              } else {
                console.warn(`No records found for Baking and Cooling cycle ${cycleNo}`);
              }
            }
          } catch (bcError) {
            console.error(`Error syncing Baking and Cooling cycle ${data.cycleNo}:`, bcError);
            totalErrors++;
          }
        }
        console.log('Baking and Cooling offline data sync completed');
      } catch (error) {
        console.error('Error syncing Baking and Cooling offline data:', error);
        totalErrors++;
      }
    } else {
      console.log('No Baking and Cooling offline data to sync');
    }

    // Sync Backing Coding offline data (one API call per cycle with all products)
    if (backingCodingOfflineData.length > 0) {
      console.log('Syncing Backing Coding offline data...');
      try {
        const uniqueCycleNos = [...new Set(backingCodingOfflineData.map((d: any) => d.cycleNo))];
        for (const cycleNo of uniqueCycleNos) {
          try {
            const plantTourId = planTourState.plantTourId;
            const userName = user?.Name || 'Current User';
            const storedStr = localStorage.getItem(`backing-coding-cycle-${cycleNo}-data`);
            const stored = storedStr ? JSON.parse(storedStr) : null;
            if (stored && Array.isArray(stored) && stored.length > 0) {
              const { savesectionApicall } = await import('../Services/BackingCodingRecord');
              const result = await savesectionApicall(stored, accessToken);
              if (result.success) {
                totalSynced += stored.length;
                localStorage.removeItem(`backing-coding-cycle-${cycleNo}-data`);
              } else {
                throw new Error(`Failed to sync Backing Coding cycle ${cycleNo}: ${result.message}`);
              }
            } else {
              const dataForCycle = backingCodingOfflineData.filter((d: any) => d.cycleNo === cycleNo);
              const { collectBackingCodingRecord, savesectionApicall } = await import('../Services/BackingCodingRecord');
              const records = dataForCycle.flatMap((d: any) =>
                (d.records || []).map((r: any) =>
                  collectBackingCodingRecord(
                    Number(cycleNo),
                    r.product || '',
                    r.executiveName || '',
                    r.sku || null,
                    r.machineProof || null,
                    r.remarks || null,
                    plantTourId || 'N/A',
                    userName,
                    null
                  )
                )
              );
              if (records.length > 0) {
                const result = await savesectionApicall(records, accessToken);
                if (result.success) totalSynced += records.length;
                else throw new Error(`Failed to sync Backing Coding cycle ${cycleNo}: ${result.message}`);
              }
            }
          } catch (bcError) {
            console.error(`Error syncing Backing Coding cycle ${cycleNo}:`, bcError);
            totalErrors++;
          }
        }
        console.log('Backing Coding offline data sync completed');
      } catch (error) {
        console.error('Error syncing Backing Coding offline data:', error);
        totalErrors++;
      }
    }

    // Sync Baking and Cooling offline files
    try {
      const state = store.getState();
      const bakingAndCoolingOfflineFiles = state.bakingAndCooling?.offlineFiles || [];
      if (bakingAndCoolingOfflineFiles.length > 0) {
        console.log('=== SYNCING BAKING AND COOLING OFFLINE FILES ===');
        console.log('Baking and Cooling offline files found:', bakingAndCoolingOfflineFiles.length);
        
        const accessToken = await getMsalToken(instance, accounts);
        if (accessToken) {
          const { syncOfflineFiles } = await import('../Services/BakingAndCoolingOfflineFileUpload');
          const fileSyncResult = await syncOfflineFiles(accessToken);
          if (fileSyncResult.success) {
            console.log('Successfully synced Baking and Cooling files');
            totalSynced += bakingAndCoolingOfflineFiles.length;
          } else {
            console.error('Failed to sync Baking and Cooling files:', fileSyncResult);
            totalErrors += bakingAndCoolingOfflineFiles.length;
          }
        } else {
          console.error('No access token available for Baking and Cooling file sync');
          totalErrors += bakingAndCoolingOfflineFiles.length;
        }
      }
    } catch (error) {
      console.error('Error syncing Baking and Cooling offline files:', error);
      totalErrors++;
    }

    // Sync Backing Coding offline files (only files with fileData - uploaded while offline)
    try {
      const state = store.getState();
      const allBackingCodingOfflineFiles = state.backingCoding?.offlineFiles || [];
      const backingCodingFilesToSync = allBackingCodingOfflineFiles.filter((f: any) => f.fileData && f.fileData.trim() !== '');
      if (backingCodingFilesToSync.length > 0) {
        console.log('Syncing Backing Coding offline files...');
        const fileToken = await getMsalToken(instance, accounts);
        if (fileToken) {
          const { syncOfflineFiles } = await import('../Services/BackingCodingOfflineFileUpload');
          const fileSyncResult = await syncOfflineFiles(fileToken);
          if (fileSyncResult.success) {
            totalSynced += backingCodingFilesToSync.length;
          } else {
            totalErrors += backingCodingFilesToSync.length;
          }
        } else {
          totalErrors += backingCodingFilesToSync.length;
        }
      }
    } catch (error) {
      console.error('Error syncing Backing Coding offline files:', error);
      totalErrors++;
    }

    // Sync Backing Mixing offline data (one API call per cycle with all products)
    if (backingMixingOfflineData.length > 0) {
      console.log('Syncing Backing Mixing offline data...');
      try {
        const uniqueCycleNos = [...new Set(backingMixingOfflineData.map((d: any) => d.cycleNo))];
        for (const cycleNo of uniqueCycleNos) {
          try {
            const plantTourId = planTourState.plantTourId;
            const userName = user?.Name || 'Current User';
            const storedStr = localStorage.getItem(`backing-mixing-cycle-${cycleNo}-data`);
            const stored = storedStr ? JSON.parse(storedStr) : null;
            if (stored && Array.isArray(stored) && stored.length > 0) {
              const { savesectionApicall } = await import('../Services/BackingMixingRecord');
              const result = await savesectionApicall(stored, accessToken);
              if (result.success) {
                totalSynced += stored.length;
                localStorage.removeItem(`backing-mixing-cycle-${cycleNo}-data`);
              } else {
                throw new Error(`Failed to sync Backing Mixing cycle ${cycleNo}: ${result.message}`);
              }
            } else {
              const dataForCycle = backingMixingOfflineData.filter((d: any) => d.cycleNo === cycleNo);
              const { collectBackingMixingRecord, savesectionApicall } = await import('../Services/BackingMixingRecord');
              const records = dataForCycle.flatMap((d: any) =>
                (d.records || []).map((r: any) =>
                  collectBackingMixingRecord(
                    Number(cycleNo),
                    r.product || '',
                    r.executiveName || '',
                    r.lineNo || null,
                    r.startTime || null,
                    r.mixingTime || null,
                    r.waterTemp || null,
                    r.yeastSlurryTemp || null,
                    r.oilTemp || null,
                    r.iceKg || null,
                    r.doughTemp || null,
                    r.dividingWeight10 || null,
                    r.seedVisibility || null,
                    plantTourId || 'N/A',
                    userName
                  )
                )
              );
              if (records.length > 0) {
                const result = await savesectionApicall(records, accessToken);
                if (result.success) totalSynced += records.length;
                else throw new Error(`Failed to sync Backing Mixing cycle ${cycleNo}: ${result.message}`);
              }
            }
          } catch (bmError) {
            console.error(`Error syncing Backing Mixing cycle ${cycleNo}:`, bmError);
            totalErrors++;
          }
        }
        console.log('Backing Mixing offline data sync completed');
      } catch (error) {
        console.error('Error syncing Backing Mixing offline data:', error);
        totalErrors++;
      }
    }

    // Sync Process Format offline data
    if (processFormatOfflineData.length > 0) {
      console.log('Syncing Process Format offline data...');
      try {
        const uniqueCycleNos = [...new Set(processFormatOfflineData.map((d: any) => d.cycleNo))];
        for (const cycleNo of uniqueCycleNos) {
          try {
            const plantTourId = planTourState.plantTourId;
            const userName = user?.Name || 'Current User';
            const storedStr = localStorage.getItem(`process-format-cycle-${cycleNo}-data`);
            const stored = storedStr ? JSON.parse(storedStr) : null;
            if (stored && Array.isArray(stored) && stored.length > 0) {
              const { savesectionApicall } = await import('../Services/ProcessFormatRecord');
              const result = await savesectionApicall(stored, accessToken);
              if (result.success) {
                totalSynced += stored.length;
                localStorage.removeItem(`process-format-cycle-${cycleNo}-data`);
              } else {
                throw new Error(`Failed to sync Process Format cycle ${cycleNo}: ${result.message}`);
              }
            } else {
              const dataForCycle = processFormatOfflineData.filter((d: any) => d.cycleNo === cycleNo);
              const { collectProcessFormatRecord, savesectionApicall } = await import('../Services/ProcessFormatRecord');
              const records: any[] = [];
              dataForCycle.forEach((d: any) => {
                (d.records || []).forEach((r: any) => {
                  (r.items || []).forEach((item: any) => {
                    records.push(
                      collectProcessFormatRecord(
                        Number(cycleNo),
                        r.product || '',
                        r.executiveName || '',
                        r.lineNo || null,
                        item.itemname || null,
                        item.noofbiscuits ? parseInt(String(item.noofbiscuits), 10) : null,
                        item.qtyused ? parseInt(String(item.qtyused), 10) : null,
                        item.status || null,
                        item.variance || null,
                        plantTourId || 'N/A',
                        userName
                      )
                    );
                  });
                });
              });
              if (records.length > 0) {
                const result = await savesectionApicall(records, accessToken);
                if (result.success) totalSynced += records.length;
                else throw new Error(`Failed to sync Process Format cycle ${cycleNo}: ${result.message}`);
              }
            }
          } catch (pfError) {
            console.error(`Error syncing Process Format cycle ${cycleNo}:`, pfError);
            totalErrors++;
          }
        }
        console.log('Process Format offline data sync completed');
      } catch (error) {
        console.error('Error syncing Process Format offline data:', error);
        totalErrors++;
      }
    }

    // Sync Proofing offline data
    if (proofingOfflineData.length > 0) {
      console.log('Syncing Proofing offline data...');
      try {
        const uniqueCycleNos = [...new Set(proofingOfflineData.map((d: any) => d.cycleNo))];
        for (const cycleNo of uniqueCycleNos) {
          try {
            const plantTourId = planTourState.plantTourId;
            const userName = user?.Name || 'Current User';
            const storedStr = localStorage.getItem(`proofing-cycle-${cycleNo}-data`);
            const stored = storedStr ? JSON.parse(storedStr) : null;
            if (stored && Array.isArray(stored) && stored.length > 0) {
              const { savesectionApicall } = await import('../Services/ProofingRecord');
              const result = await savesectionApicall(stored, accessToken);
              if (result.success) {
                totalSynced += stored.length;
                localStorage.removeItem(`proofing-cycle-${cycleNo}-data`);
              } else {
                throw new Error(`Failed to sync Proofing cycle ${cycleNo}: ${result.message}`);
              }
            } else {
              const dataForCycle = proofingOfflineData.filter((d: any) => d.cycleNo === cycleNo);
              const { collectProofingRecord, savesectionApicall } = await import('../Services/ProofingRecord');
              const records: any[] = [];
              dataForCycle.forEach((d: any) => {
                (d.records || []).forEach((r: any) => {
                  (r.rows || []).forEach((row: any) => {
                    records.push(
                      collectProofingRecord(
                        Number(cycleNo),
                        r.product || '',
                        r.executiveName || '',
                        r.lineNumber || null,
                        r.observedtime || '',
                        row.variety || null,
                        row.proofingTime || null,
                        row.prooferTemp || null,
                        row.rhProofer || null,
                        plantTourId || 'N/A',
                        userName
                      )
                    );
                  });
                });
              });
              if (records.length > 0) {
                const result = await savesectionApicall(records, accessToken);
                if (result.success) totalSynced += records.length;
                else throw new Error(`Failed to sync Proofing cycle ${cycleNo}: ${result.message}`);
              }
            }
          } catch (proofError) {
            console.error(`Error syncing Proofing cycle ${cycleNo}:`, proofError);
            totalErrors++;
          }
        }
        console.log('Proofing offline data sync completed');
      } catch (error) {
        console.error('Error syncing Proofing offline data:', error);
        totalErrors++;
      }
    }

    // Sync Baking Sieves And Magnets offline data
    if (bakingSievesAndMagnetsOfflineData.length > 0) {
      console.log('Syncing Baking Sieves And Magnets offline data...');
      try {
        const uniqueCycleNos = [...new Set(bakingSievesAndMagnetsOfflineData.map((d: any) => d.cycleNo))];
        for (const cycleNo of uniqueCycleNos) {
          try {
            const plantTourId = planTourState.plantTourId;
            const userName = user?.Name || 'Current User';
            const storedStr = localStorage.getItem(`baking-sieves-magnets-cycle-${cycleNo}-data`);
            const stored = storedStr ? JSON.parse(storedStr) : null;
            if (stored && Array.isArray(stored) && stored.length > 0) {
              const { savesectionApicall } = await import('../Services/BakingSievesAndMagnetsRecord');
              const result = await savesectionApicall(stored, accessToken);
              if (result.success) {
                totalSynced += stored.length;
                localStorage.removeItem(`baking-sieves-magnets-cycle-${cycleNo}-data`);
              } else {
                throw new Error(`Failed to sync Baking Sieves And Magnets cycle ${cycleNo}: ${result.message}`);
              }
            } else {
              const dataForCycle = bakingSievesAndMagnetsOfflineData.filter((d: any) => d.cycleNo === cycleNo);
              const { collectBakingSievesAndMagnetsRecord, savesectionApicall } = await import('../Services/BakingSievesAndMagnetsRecord');
              const records: any[] = [];
              dataForCycle.forEach((d: any) => {
                (d.records || []).forEach((r: any) => {
                  (r.items || []).forEach((item: any) => {
                    records.push(
                      collectBakingSievesAndMagnetsRecord(
                        Number(cycleNo),
                        item.criteria || '',
                        item.title || '',
                        r.timeValue || '',
                        r.executiveName || '',
                        item.status === 'not-okay' ? (item.remarks || 'Defect noted') : null,
                        plantTourId || 'N/A',
                        userName
                      )
                    );
                  });
                });
              });
              if (records.length > 0) {
                const result = await savesectionApicall(records, accessToken);
                if (result.success) totalSynced += records.length;
                else throw new Error(`Failed to sync Baking Sieves And Magnets cycle ${cycleNo}: ${result.message}`);
              }
            }
          } catch (bsmError) {
            console.error(`Error syncing Baking Sieves And Magnets cycle ${cycleNo}:`, bsmError);
            totalErrors++;
          }
        }
        console.log('Baking Sieves And Magnets offline data sync completed');
      } catch (error) {
        console.error('Error syncing Baking Sieves And Magnets offline data:', error);
        totalErrors++;
      }
    }

    // Sync Net Weight Monitoring offline data
    console.log('=== CHECKING NET WEIGHT MONITORING OFFLINE DATA ===');
    console.log('Net Weight Monitoring offline data length:', netWeightOfflineData.length);
    console.log('Net Weight Monitoring offline data:', netWeightOfflineData);
    
    if (netWeightOfflineData.length > 0) {
      console.log('Syncing Net Weight Monitoring offline data...');
      console.log('Net Weight Monitoring pending sync data:', netWeightOfflineData);
      try {
        for (const data of netWeightOfflineData) {
          try {
            const cycleNo = data.cycleNo;
            const plantTourId = planTourState.plantTourId;
            const userName = user?.Name || 'Current User';
            const selectedShift = planTourState.selectedCycle;

            console.log(`Syncing Net Weight Monitoring cycle ${cycleNo}:`, data);
            console.log('Using data for Net Weight Monitoring sync:', {
              cycleNo: data.cycleNo,
              plantTourId,
              userName,
              selectedShift,
              recordsCount: data.records.length
            });
            console.log('Net Weight Monitoring records data:', data.records);
            console.log('First record details:', data.records[0]);
            if (data.records[0]) {
              console.log('First record mc1:', data.records[0].mc1);
              console.log('First record mc2:', data.records[0].mc2);
              console.log('First record mc3:', data.records[0].mc3);
              console.log('First record mc4:', data.records[0].mc4);
            }

            // Import the sync function from NetWeightMonitoringRecord service
            console.log('Importing syncOfflineDataToBackend function...');
            const { syncOfflineDataToBackend } = await import('../Services/NetWeightMonitoringRecord');
            console.log('syncOfflineDataToBackend function imported successfully');
            
            // Sync the offline data
            console.log('Calling syncOfflineDataToBackend with data:', [data]);
            const response = await syncOfflineDataToBackend(
              [data], // Pass as array since the function expects OfflineSavedData[]
              plantTourId || 'N/A',
              userName,
              selectedShift
            );
            console.log('syncOfflineDataToBackend response:', response);

            if (response.success) {
              console.log(`Successfully synced Net Weight Monitoring cycle ${cycleNo}`);
              totalSynced++;
            } else {
              throw new Error(`Failed to sync Net Weight Monitoring cycle ${cycleNo}: ${response.message}`);
            }
          } catch (nwmError) {
            console.error(`Failed to sync Net Weight Monitoring cycle ${data.cycleNo}:`, nwmError);
            totalErrors++;
          }
        }

        console.log('Net Weight Monitoring offline data sync completed');
      } catch (error) {
        console.error('Error syncing Net Weight Monitoring offline data:', error);
        totalErrors++;
      }
    } else {
      console.log('No Net Weight Monitoring offline data to sync');
    }

    // Sync OPRP and CCP offline data
    console.log('=== CHECKING OPRP AND CCP OFFLINE DATA ===');
    console.log('OPRP and CCP offline data length:', oprpAndCcpOfflineData.length);
    console.log('OPRP and CCP offline data:', oprpAndCcpOfflineData);
    
    if (oprpAndCcpOfflineData.length > 0) {
      console.log('Syncing OPRP and CCP offline data...');
      console.log('OPRP and CCP pending sync data:', oprpAndCcpOfflineData);
      try {
        for (const data of oprpAndCcpOfflineData) {
          try {
            const cycleNo = data.cycleNo;
            const plantTourId = planTourState.plantTourId;
            const userName = user?.Name || 'Current User';
            const selectedShift = planTourState.selectedCycle;

            console.log(`Syncing OPRP and CCP cycle ${cycleNo}:`, data);
            console.log('Using data for OPRP and CCP sync:', {
              cycleNo: data.cycleNo,
              plantTourId,
              userName,
              selectedShift,
              recordsCount: data.records.length
            });

            // Import the sync function from OPRPAndCCPRecord service
            console.log('Importing syncOfflineDataToBackend function...');
            const { syncOfflineDataToBackend } = await import('../Services/OPRPAndCCPRecord');
            console.log('syncOfflineDataToBackend function imported successfully');
            
            // Sync the offline data
            console.log('Calling syncOfflineDataToBackend with data:', [data]);
            const response = await syncOfflineDataToBackend(
              [data], // Pass as array since the function expects OfflineSavedData[]
              plantTourId || 'N/A',
              userName,
              selectedShift
            );
            console.log('syncOfflineDataToBackend response:', response);

            if (response.success) {
              console.log(`Successfully synced OPRP and CCP cycle ${cycleNo}`);
              totalSynced++;
            } else {
              throw new Error(`Failed to sync OPRP and CCP cycle ${cycleNo}: ${response.message}`);
            }
          } catch (oprpError) {
            console.error(`Failed to sync OPRP and CCP cycle ${data.cycleNo}:`, oprpError);
            totalErrors++;
          }
        }

        console.log('OPRP and CCP offline data sync completed');
      } catch (error) {
        console.error('Error syncing OPRP and CCP offline data:', error);
        totalErrors++;
      }
    } else {
      console.log('No OPRP and CCP offline data to sync');
    }

    // Show sync results and clear data only if ALL sync operations were successful
    if (totalSynced > 0 && totalErrors === 0) {
      alert(`✅ Successfully synced ${totalSynced} offline data item(s)!`);
      
      // Refresh cycle data from SharePoint after successful sync
      // This ensures the newly synced data appears in SharePoint
      const plantTourId = planTourState.plantTourId;
      if (plantTourId && accessToken) {
        console.log('Refreshing cycle data from SharePoint after sync...');
        try {
          // Wait a moment for database to update
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          const [summaryData, cycleData] = await Promise.all([
            fetchSummaryData(accessToken, plantTourId),
            fetchCycleDetails(accessToken, plantTourId)
          ]);
          
          const summaryArray = summaryData && Array.isArray(summaryData) ? summaryData : [];
          const cycleArray = cycleData && Array.isArray(cycleData) ? cycleData : [];
          
          console.log("Refreshed Summary Data after sync:", summaryArray.length, "records");
          console.log("Refreshed Cycle Records after sync:", cycleArray.length, "records");
          
          // Store refreshed data in Redux
          dispatch(setSummaryData(summaryArray));
          dispatch(setCycleData(cycleArray));
          dispatch(setLastFetchTimestamp(Date.now()));
          
          // Process and store category summary and cycle count
          if (summaryArray.length > 0) {
            const processed = processData(summaryArray);
            dispatch(setCategorySummary(processed.summary));
            dispatch(setCycleCount(processed.totalCycles));
            console.log("Refreshed category summary and cycle count stored in Redux");
          }
          
          console.log('Cycle data refreshed from SharePoint successfully');
        } catch (refreshError) {
          console.error('Error refreshing cycle data after sync:', refreshError);
          // Don't fail the sync if refresh fails - data is already saved
        }
      }
      
      // Clear all Redux data only after successful sync
      console.log('All sync operations successful. Clearing Redux data...');
      
      // Clear all Redux data except token, plantTourId, employeeDetails, and user details
      dispatch(clearAllDataExceptEssential());
      dispatch(resetOfflineState());

      // Clear cream percentage Redux state
      dispatch(resetCreamPercentage());
      
      // Clear Sieve and Magnet New Plant Redux state
      dispatch(clearSieveAndMagnetNewPlantData());
      
      // Clear Sieve and Magnet Old Plant Redux state
      dispatch(clearSieveAndMagnetOldPlantData());
      
      // Clear Product Monitoring Redux state
      dispatch(clearProductMonitoringData());
      
      // Clear Code Verification Redux state
      dispatch(clearOfflineData());
      // Clear Baking Process Redux state
      dispatch(clearBakingOfflineData());
      // Clear Seal Integrity Test Redux state
      dispatch(clearSealIntegrityOfflineData());
      // Clear ALC Redux state
      dispatch(clearALCOfflineData());
      // Clear Net Weight Monitoring Redux state
      dispatch(clearNWMOfflineData());
      // Clear OPRP and CCP Redux state
      dispatch(clearOPRPAndCcpOfflineData());
      // Clear Baking and Cooling Redux state
      dispatch(clearBakingAndCoolingOfflineData());
      // Clear Backing Coding Redux state (including fetchedCycles for fresh start)
      dispatch(resetBackingCodingState());
      // Clear Backing Mixing Redux state (including fetchedCycles for fresh start)
      dispatch(resetBackingMixingState());
      dispatch(resetProcessFormatState());
      dispatch(resetProofingState());
      dispatch(resetBakingSievesAndMagnetsState());
      dispatch(resetBackingPQIState());

      setShowOfflineError(false);

      // Clear any offline data from localStorage
      try {
        localStorage.removeItem('offlineData');
        localStorage.removeItem('offlineSubmissions');
        console.log('Offline data cleared from localStorage');
      } catch (error) {
        console.error('Error clearing offline data:', error);
      }

      console.log('All data cleared except essential information (token, plantTourId, employeeDetails, user details)');
      console.log('Offline mode canceled, normal plant tour is now available');
      
    } else if (totalSynced > 0 && totalErrors > 0) {
      alert(`⚠️ Synced ${totalSynced} item(s) successfully, but ${totalErrors} item(s) failed. Please check console for details.`);
      console.log('Some sync operations failed. Keeping Redux data intact.');
    } else if (totalSynced === 0 && totalErrors > 0) {
      alert('❌ Failed to sync offline data. Please check console for details.');
      console.log('All sync operations failed. Keeping Redux data intact.');
      return;
    } else {
      console.log('No offline data to sync');
      // Even if no data to sync, we can clear the Redux state to reset everything
      console.log('No offline data found. Clearing Redux data to reset state...');
      
      // Clear all Redux data except token, plantTourId, employeeDetails, and user details
      dispatch(clearAllDataExceptEssential());
      dispatch(resetOfflineState());

      // Clear cream percentage Redux state
      dispatch(resetCreamPercentage());
      
      // Clear Sieve and Magnet New Plant Redux state
      dispatch(clearSieveAndMagnetNewPlantData());
      
      // Clear Sieve and Magnet Old Plant Redux state
      dispatch(clearSieveAndMagnetOldPlantData());
      
      // Clear Product Monitoring Redux state
      dispatch(clearProductMonitoringData());
      
      // Clear Code Verification Redux state
      dispatch(clearOfflineData());
      // Clear Baking Process Redux state
      dispatch(clearBakingOfflineData());
      // Clear Seal Integrity Test Redux state
      dispatch(clearSealIntegrityOfflineData());
      // Clear ALC Redux state
      dispatch(clearALCOfflineData());
      // Clear OPRP and CCP Redux state
      dispatch(clearOPRPAndCcpOfflineData());
      // Clear Baking and Cooling Redux state
      dispatch(clearBakingAndCoolingOfflineData());
      // Clear Backing Coding Redux state (including fetchedCycles for fresh start)
      dispatch(resetBackingCodingState());
      // Clear Backing Mixing Redux state (including fetchedCycles for fresh start)
      dispatch(resetBackingMixingState());
      dispatch(resetProcessFormatState());
      dispatch(resetProofingState());
      dispatch(resetBakingSievesAndMagnetsState());
      dispatch(resetBackingPQIState());

      setShowOfflineError(false);

      // Clear any offline data from localStorage
      try {
        localStorage.removeItem('offlineData');
        localStorage.removeItem('offlineSubmissions');
        console.log('Offline data cleared from localStorage');
      } catch (error) {
        console.error('Error clearing offline data:', error);
      }

      console.log('All data cleared except essential information (token, plantTourId, employeeDetails, user details)');
      console.log('Offline mode canceled, normal plant tour is now available');
    }
  };

  useEffect(() => {
    if (accounts.length > 0) {
      instance
        .acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
        })
        .then((response) => {
          fetchEmployeeList(response.accessToken, user?.Name).then((res) => {
            setEmployees(res);
            if (res && res.length > 0) {
              dispatch(setEmployeeDetails(res[0]));
            }
          });
        })
        .catch((error) => {
          console.error("Token acquisition failed", error);
        });
    }
  }, [accounts, instance, user?.Name]);

  const handleLogout = () => {
    console.log("Logging out...");

    // Clear all Redux state
    dispatch(clearAllDataExceptEssential());
    dispatch(resetOfflineState());
    dispatch(clearUser());
    dispatch(resetCreamPercentage());
    dispatch(clearSieveAndMagnetNewPlantData());
    dispatch(clearSieveAndMagnetOldPlantData());
    dispatch(clearProductMonitoringData());
    dispatch(clearOfflineData());

    // Clear localStorage (but preserve Redux persistence for offline data)
    localStorage.removeItem('accessToken');
    localStorage.removeItem('tokenExpiry');
    localStorage.removeItem('offlineData');
    // Don't clear redux-persist:root as it contains offline data
    // localStorage.removeItem('redux-persist:root');

    // Clear sessionStorage
    sessionStorage.clear();

    // Perform MSAL logout with redirect to login page
    instance.logoutPopup({
      postLogoutRedirectUri: window.location.origin
    });

    // Navigate to login page
    navigate("/", { replace: true });
  };


  function pad(num: number): string {
    return num.toString().padStart(2, "0");
  }

  // Monitor network status
  useEffect(() => {
    const handleOnline = () => {
      console.log('🌐 Internet Connected:', new Date().toISOString());
      setIsOnline(true);
    };

    const handleOffline = () => {
      console.log('❌ Internet Disconnected:', new Date().toISOString());
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    startTimer();
  }, []);

  // Debug: Check persisted offline data on component mount
  useEffect(() => {
    console.log('HomePage: Component mounted - checking persisted offline data');
    console.log('HomePage: sieveAndMagnetOldPlantPendingSync length:', sieveAndMagnetOldPlantPendingSync?.length);
    console.log('HomePage: codeVerificationOfflineData length:', codeVerificationOfflineData?.length);
    console.log('HomePage: codeVerificationOfflineData details:', codeVerificationOfflineData);
    console.log('HomePage: All offline data counts:', {
      offlineSubmissions: offlineSubmissions.length,
      creamPercentagePendingSync: creamPercentagePendingSync.length,
      sieveAndMagnetNewPlantPendingSync: sieveAndMagnetNewPlantPendingSync.length,
      sieveAndMagnetOldPlantPendingSync: sieveAndMagnetOldPlantPendingSync.length,
      productMonitoringPendingSync: productMonitoringPendingSync.length,
      codeVerificationOfflineData: codeVerificationOfflineData.length
    });

    // Check localStorage for Redux persistence
    try {
      const reduxPersistData = localStorage.getItem('redux-persist:root');
      if (reduxPersistData) {
        const parsedData = JSON.parse(reduxPersistData);
        console.log('HomePage: Redux persistence data found',parsedData);
      } else {
        console.log('HomePage: No Redux persistence data found');
      }
    } catch (error) {
      console.error('HomePage: Error reading Redux persistence data:', error);
    }
  }, [codeVerificationOfflineData]);

  function startTimer(): void {
    const timerElement = document.getElementById("timer");

    if (!timerElement) {
      console.error("Timer element not found!");
      return;
    }

    setInterval(() => {
      const now = new Date();
      const hours = pad(now.getHours());
      const minutes = pad(now.getMinutes());
      const seconds = pad(now.getSeconds());

      timerElement.textContent = `${hours}:${minutes}:${seconds}`;
    }, 1000);
  }

  // Format date as DD/MM/YYYY
  const now = new Date();
  const day = String(now.getDate()).padStart(2, '0');
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const year = now.getFullYear();
  const formattedDate = `${day}/${month}/${year}`;
  console.log(isOfflineCompleted);

  // Add handler for starting plant tour
  const handleStartPlantTour = async (selectedTour: string, selectedShift: string) => {
    console.log('handleStartPlantTour called with:', { selectedTour, selectedShift });
    const employee = planTourState.employeeDetails;

    console.log('Current Redux state:', {
      employee: employee,
      selectedTour: selectedTour,
      selectedShift: selectedShift,
      user: user
    });

    if (!employee || !user || !selectedTour || !selectedShift) {
      console.log('Missing required data:', { employee: !!employee, user: !!user, selectedTour: !!selectedTour, selectedShift: !!selectedShift });
      return;
    }

    setIsPlantTourLoading(true);
    try {
      // Check if we're in offline mode
      if (isOfflineStarted) {
        console.log('Starting offline plant tour...');
        // For offline mode, use existing plantTourId from Redux
        const existingPlantTourId = planTourState.plantTourId;
        if (existingPlantTourId) {
          console.log('Using existing plant tour ID for offline mode:', existingPlantTourId);
          setIsModalOpen(false);
          // Navigate based on selected tour type
          console.log('Selected tour:', selectedTour);
          if (selectedTour === "Cream Percentage Checklist" || selectedTour === "Cream Percentage Index") {
            console.log('Navigating to Cream Percentage Index');
            navigate("/creampercentage");
          } else if (selectedTour === "Sieves and magnets old plant" || selectedTour === "Sieves and magnets Line-1 plant") {
            console.log('Navigating to Sieve and Magnet Old Plant');
            navigate("/sieveandmagnetoldplant");
          } else if (selectedTour === "Sieves and magnets new plant" || selectedTour === "Sieves and magnets Line-2 plant") {
            console.log('Navigating to Sieve and Magnet New Plant');
            navigate("/sieveandmagnetnewplant");
          } else if (selectedTour === "Product Monitoring Record") {
            console.log('Navigating to Product Monitoring Record');
            navigate("/productmonitoringrecord");
          } else if (selectedTour === "Net Weight Monitoring Record") {
            console.log('Navigating to Net Weight Monitoring Record');
            navigate("/netweightmonitoringrecord");
          } else if (selectedTour === "Code Verification Record") {
            console.log('Navigating to Code Verification Record');
            navigate("/codeverificationrecord");
          } else if (selectedTour === "OPRP and CCP Record") {
            console.log('Navigating to OPRP and CCP Record');
            navigate("/oprpandccprecord");
          } else if (selectedTour === "Baking Process Record") {
            console.log('Navigating to Baking Process Record');
            navigate("/bakingprocessrecord");
          } else if (selectedTour === "Seal Integrity Test") {
            console.log('Navigating to Seal Integrity Test');
            navigate("/sealintegritytest");
          } else if (selectedTour === "Area Line Clearance Checklist" || selectedTour === "ALC") {
            console.log('Navigating to ALC');
            navigate("/alc");
          } else if (selectedTour === "Quality Wall Record") {
            console.log('Navigating to Quality Wall Record');
            navigate("/qualitywallrecord");
          } else if (selectedTour === "Mixing parameter") {
            console.log('Navigating to Mixing parameter');
            navigate("/processparameterverification");
          } else {
            console.log('Navigating to Product Quality Index');
            navigate("/qualityplantour");
          }
        } else {
          throw new Error('No plant tour ID available for offline mode');
        }
      } else {
        console.log('Starting online plant tour...');
        // For online mode, create or fetch new plant tour ID
        const tokenResult = await getAccessToken();
        const accessToken = tokenResult?.token;
       
        if (!accessToken) throw new Error('No access token available');
        const plantTourId = await createOrFetchPlantTour({
          accessToken,
          departmentId: employee.departmentId,
          employeeName: employee.employeeName,
          roleName: employee.roleName,
          plantId: employee.plantId,
          userRoleID: employee.roleId,
        });
        if (plantTourId) {
          dispatch(setPlantTourId(plantTourId));
          setIsModalOpen(false);
          // Navigate based on selected tour type
          console.log('Selected tour:', selectedTour);
          if (selectedTour === "Cream Percentage Checklist" || selectedTour === "Cream Percentage Index") {
            console.log('Navigating to Cream Percentage Index');
            navigate("/creampercentage");
          } else if (selectedTour === "Sieves and magnets old plant" || selectedTour === "Sieves and magnets Line-1 plant") {
            console.log('Navigating to Sieve and Magnet Old Plant');
            navigate("/sieveandmagnetoldplant");  
          } else if (selectedTour === "Sieves and magnets new plant" || selectedTour === "Sieves and magnets Line-2 plant") {
            console.log('Navigating to Sieve and Magnet New Plant');
            navigate("/sieveandmagnetnewplant");
          } else if (selectedTour === "Product Monitoring Record") {
            console.log('Navigating to Product Monitoring Record');
            navigate("/productmonitoringrecord");
          } else if (selectedTour === "Net Weight Monitoring Record") {
            console.log('Navigating to Net Weight Monitoring Record');
            navigate("/netweightmonitoringrecord");
          } else if (selectedTour === "Code Verification Record") {
            console.log('Navigating to Code Verification Record');
            navigate("/codeverificationrecord");
          } else if (selectedTour === "OPRP and CCP Record") {
            console.log('Navigating to OPRP and CCP Record');
            navigate("/oprpandccprecord");
          } else if (selectedTour === "Baking Process Record") {
            console.log('Navigating to Baking Process Record');
            navigate("/bakingprocessrecord");
          } else if (selectedTour === "Seal Integrity Test") {
            console.log('Navigating to Seal Integrity Test');
            navigate("/sealintegritytest");
          } else if (selectedTour === "Area Line Clearance Checklist" || selectedTour === "ALC") {
            console.log('Navigating to ALC');
            navigate("/alc");
          } else if (selectedTour === "Quality Wall Record") {
            console.log('Navigating to Quality Wall Record');
            navigate("/qualitywallrecord");
          } else if (selectedTour === "Mixing parameter") {
            console.log('Navigating to Mixing parameter');
            navigate("/processparameterverification");
          } else {
            console.log('Navigating to Product Quality Index');
            navigate("/qualityplantour");
          }
        }
      }
    } catch (err) {
      console.error("Failed to start plant tour", err);
      alert('Failed to start plant tour. Please try again.');
    } finally {
      setIsPlantTourLoading(false);
    }
  };

  // Handler for Bakery Noida modal
  const handleBakeryNoidaPlantTour = async (selectedTour: string, selectedShift: string) => {
    console.log('handleBakeryNoidaPlantTour called with:', { selectedTour, selectedShift });
    console.log('selectedTour type:', typeof selectedTour, 'value:', JSON.stringify(selectedTour));
    console.log('Will navigate to Coding?', selectedTour === "Coding", selectedTour.trim() === "Coding");
    setIsBakeryNoidaModalOpen(false);
    // The tour and shift are already stored in Redux by the modal
    
    try {
      setIsPlantTourLoading(true);
      dispatch(setProgress(0));
      
      // CRITICAL: Check if offline mode is active - if so, skip ALL API calls
      const isOfflineMode = store.getState().appState?.isOfflineStarted || false;
      if (isOfflineMode) {
        console.log('=== OFFLINE MODE ACTIVE - SKIPPING ALL API CALLS ===');
        console.log('Using cached data from Redux for offline plant tour');
        
        // Get cached plant tour ID from Redux
        const cachedPlantTourId = store.getState().planTour?.plantTourId;
        if (cachedPlantTourId) {
          console.log('Using cached plant tour ID:', cachedPlantTourId);
          dispatch(setProgress(100));
          setIsPlantTourLoading(false);
          
          // Navigate based on selected tour
          const tourTrimmed = selectedTour?.trim();
          console.log('Offline mode navigation - tourTrimmed:', tourTrimmed);
          if (tourTrimmed === "Baking & Cooling") {
            console.log('Navigating to Baking and Cooling page (offline mode)');
            navigate("/bakingandcooling");
          } else if (tourTrimmed === "Coding") {
            console.log('Navigating to Backing Coding page (offline mode)');
            navigate("/backingcoding");
          } else if (tourTrimmed === "Mixing") {
            console.log('Navigating to Backing Mixing page (offline mode)');
            navigate("/backingmixing");
          } else if (tourTrimmed === "Process Format") {
            console.log('Navigating to Process Format page (offline mode)');
            navigate("/processformat");
          } else if (tourTrimmed === "Proofing") {
            console.log('Navigating to Proofing page (offline mode)');
            navigate("/proofing");
          } else if (tourTrimmed === "Sieves and magnets") {
            console.log('Navigating to Baking Sieves And Magnets page (offline mode)');
            navigate("/bakingsievesandmagnets");
          } else if (tourTrimmed === "PQI") {
            console.log('Navigating to Backing PQI page (offline mode)');
            navigate("/backing-pqi");
          } else {
            console.log('Navigating to qualityplantour (offline mode, fallback)');
            navigate("/qualityplantour");
          }
          return; // Exit early - no API calls
        } else {
          throw new Error('No cached plant tour ID found. Please start offline mode first.');
        }
      }
      
      // Check if user has required Name property
      if (!user?.Name) {
        throw new Error('User name not found. Please ensure you are logged in properly.');
      }

      // Step 1: Get access tokens - ONLY if not offline
      dispatch(setProgress(20));
      const tokenResult = await getAccessToken();
      const accessToken = tokenResult?.token;
      
      if (!accessToken) {
        throw new Error('Failed to get access token');
      }

      // Get SharePoint token for employee list
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });
      const sharepointToken = response.accessToken;

      // Step 2: Fetch employee details
      dispatch(setProgress(40));
      const employeeList = await fetchEmployeeList(sharepointToken, user.Name);
      let currentEmployeeDetails: any;
      if (employeeList && employeeList.length > 0) {
        currentEmployeeDetails = employeeList[0];
      } else {
        console.warn('Employee not found in EmployeeList. Using fallback for plant tour.');
        currentEmployeeDetails = {
          employeeName: user.Name.trim(),
          departmentId: '135',
          departmentName: 'N/A',
          roleName: 'QA',
          roleId: '1',
          plantId: '1',
          plantName: 'N/A',
        };
      }

      // Step 3: Create or fetch department tour ID (retry once with fresh token on 401)
      dispatch(setProgress(60));
      let departmentTourId: string | null = null;
      let lastError: Error | null = null;
      let tokenToUse = accessToken;
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          departmentTourId = await createOrFetchDepartmentTour({
            accessToken: tokenToUse,
            departmentId: currentEmployeeDetails.departmentId || '135',
            employeeName: currentEmployeeDetails.employeeName || user?.Name || '',
            roleName: currentEmployeeDetails.roleName || 'QA',
            plantId: currentEmployeeDetails.plantId || '1',
            userRoleID: currentEmployeeDetails.roleId || '1',
          });
          if (departmentTourId) break;
        } catch (err) {
          lastError = err instanceof Error ? err : new Error(String(err));
          const msg = lastError.message || '';
          if (attempt === 0 && (msg.includes('401') || msg.includes('Unauthorized'))) {
            console.warn('Department tour API returned 401, clearing cached token and retrying with fresh token...');
            localStorage.removeItem('access_token');
            const freshResult = await getAccessToken();
            tokenToUse = freshResult?.token || '';
            if (!tokenToUse) break;
          } else {
            throw lastError;
          }
        }
      }

      if (!departmentTourId) {
        throw lastError || new Error('Failed to create or fetch department tour ID');
      }

      // Step 4: Store department tour ID in Redux
      dispatch(setProgress(80));
      dispatch(setPlantTourId(departmentTourId));
      localStorage.setItem('plantTourId', departmentTourId);
      dispatch(setPlantTourSectionPlantTourId(departmentTourId));

      // Step 5: Store employee details
      dispatch(setEmployeeDetails(currentEmployeeDetails));

      dispatch(setProgress(100));
      
      // Navigate based on selected tour type
      const tourTrimmed = selectedTour?.trim();
      console.log('Online mode navigation - tourTrimmed:', tourTrimmed);
      if (tourTrimmed === "Baking & Cooling") {
        console.log('Navigating to Baking and Cooling page');
        navigate("/bakingandcooling");
      } else if (tourTrimmed === "Coding") {
        console.log('Navigating to Backing Coding page');
        navigate("/backingcoding");
      } else if (tourTrimmed === "Mixing") {
        console.log('Navigating to Backing Mixing page');
        navigate("/backingmixing");
      } else if (tourTrimmed === "Process Format") {
        console.log('Navigating to Process Format page');
        navigate("/processformat");
      } else if (tourTrimmed === "Proofing") {
        console.log('Navigating to Proofing page');
        navigate("/proofing");
      } else if (tourTrimmed === "Sieves and magnets") {
        console.log('Navigating to Baking Sieves And Magnets page');
        navigate("/bakingsievesandmagnets");
      } else if (tourTrimmed === "PQI") {
        console.log('Navigating to Backing PQI page');
        navigate("/backing-pqi");
      } else {
        console.log('Navigating to plant-tour-section (fallback)');
        navigate("/plant-tour-section");
      }
    } catch (error) {
      console.error('Error starting plant tour:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      alert(`Failed to start plant tour: ${errorMessage}`);
      
      // Still navigate even if there's an error (fallback navigation)
      const tourTrimmed = selectedTour?.trim();
      console.log('Attempting fallback navigation for:', tourTrimmed);
      try {
        if (tourTrimmed === "Baking & Cooling") {
          console.log('Fallback: Navigating to Baking and Cooling page');
          navigate("/bakingandcooling");
        } else if (tourTrimmed === "Coding") {
          console.log('Fallback: Navigating to Backing Coding page');
          navigate("/backingcoding");
        } else if (tourTrimmed === "Mixing") {
          console.log('Fallback: Navigating to Backing Mixing page');
          navigate("/backingmixing");
        } else if (tourTrimmed === "Process Format") {
          console.log('Fallback: Navigating to Process Format page');
          navigate("/processformat");
        } else if (tourTrimmed === "Proofing") {
          console.log('Fallback: Navigating to Proofing page');
          navigate("/proofing");
        } else if (tourTrimmed === "Sieves and magnets") {
          console.log('Fallback: Navigating to Baking Sieves And Magnets page');
          navigate("/bakingsievesandmagnets");
        } else if (tourTrimmed === "PQI") {
          console.log('Fallback: Navigating to Backing PQI page');
          navigate("/backing-pqi");
        } else {
          console.log('Fallback: Navigating to plant-tour-section');
          navigate("/plant-tour-section");
        }
      } catch (navError) {
        console.error('Fallback navigation also failed:', navError);
      }
    } finally {
      setIsPlantTourLoading(false);
    }
  };

  console.log(user.DVAccessToken, 'user.DVAccessToken');


  return (
    <DashboardLayout rightContent={<p className="text-gray-500 dark:text-gray-400 text-sm font-semibold">{getDepartmentName()}</p>} onLogout={handleLogout}>
      <div>
        {/* Content */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <h2 className="text-lg sm:text-xl font-medium">Hello, {user?.Name}</h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              <span id="timer"></span> {formattedDate}
            </p>
          </div>
          

          {/* Buttons Section */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full sm:w-auto">
            {!isOfflineStarted && (
              <>
                <button
                  className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
                  onClick={() => {
                    const department = getDepartmentName();
                    if (department === "Quality - Rajpura" || department === "Quality - Indore" || department === "Quality - Phillaur") {
                      setIsModalOpen(true);
                    } else if (department === "Bakery - Noida") {
                      setIsBakeryNoidaModalOpen(true);
                    } else {
                      handleOtherDepartmentPlantTour();
                    }
                  }}
                  disabled={isPlantTourLoading}
                >
                  Plant Tour
                </button>

                <button
                  className={`w-full sm:w-auto px-4 py-2 rounded-md ${isOnline
                      ? 'bg-gray-600 hover:bg-gray-700 text-white dark:bg-gray-700 dark:hover:bg-gray-600'
                      : 'bg-gray-400 text-gray-600 cursor-not-allowed'
                    }`}
                  onClick={() => {
                    if (getDepartmentName() === "Quality - Rajpura" || getDepartmentName () === "Quality - Indore" || getDepartmentName() === "Quality - Phillaur") {
                      handleOfflineTour();
                    } else {
                      handleOtherDepartmentStartOfflinePlantTour();
                    }
                  }}
                  disabled={!isOnline}
                  title={!isOnline ? 'Internet connection required to start offline mode' : 'Start offline mode'}
                >
                  + Start Offline Mode 
                </button>
                {showOfflineError && (
                  <div className="w-full sm:w-auto text-xs text-red-600 mt-1">
                    ⚠️ Internet connection required to start offline mode
                  </div>
                )}
              </>
            )}
            {isOfflineStarted && (
              <>
                <button
                  className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
                  onClick={() => {
                    const department = getDepartmentName();
                    console.log('Offline Plant Tour button clicked, department:', department);
                    if (department === "Quality - Rajpura" || department === "Quality - Indore" || department === "Quality - Phillaur") {
                      console.log('Opening modal for Quality department');
                      setIsModalOpen(true);
                    } else if (department === "Bakery - Noida") {
                      console.log('Opening modal for Bakery - Noida department');
                      setIsBakeryNoidaModalOpen(true);
                    } else {
                      console.log('Navigating to plant-tour-section for other department:', department);
                      handleOtherDepartmentOfflinePlantTour();
                    }
                  }}
                  disabled={isPlantTourLoading}
                >
                  + Offline Plant Tour
                </button>

                <button
                  className={`w-full sm:w-auto px-4 py-2 rounded-md ${isOnline
                      ? 'bg-gray-600 hover:bg-gray-700 text-white dark:bg-gray-700 dark:hover:bg-gray-600'
                      : 'bg-gray-400 text-gray-600 cursor-not-allowed'
                    }`}
                  onClick={() => {
                    if (getDepartmentName() === "Quality - Rajpura") {
                      handleCancelOffline();
                    } else {
                      handleOtherDepartmentSyncCancelOffline();
                    }
                  }}
                  disabled={!isOnline}
                  title={!isOnline ? 'Internet connection required to sync offline data' : 'Sync and cancel offline mode'}
                >
                  + Sync & Cancel Offline {totalOfflineCount > 0 && `(${totalOfflineCount})`}
                  {!isOnline && <span className="ml-1 text-xs">(Internet Required)</span>}
                </button>
                {totalOfflineCount > 0 && !isOnline && (
                  <div className="w-full sm:w-auto text-xs text-orange-600 mt-1">
                    ⚠️ {totalOfflineCount} offline submission(s) waiting for internet connection
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        {/* Metric Cards */}
        <div className="mb-6 overflow-x-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 min-w-0">
            {metrics.map((item, index) => (
              <div
                key={index}
                className="bg-gray-100 dark:bg-gray-800 min-w-[180px] p-4 rounded-lg shadow flex flex-col items-center text-center"
              >
                <div className="bg-orange-100 dark:bg-orange-200 p-2 rounded-full mb-2">
                  {item.icon}
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-300">{item.label}</p>
                <p className="text-xl font-semibold">{item.count}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="mb-4">
          <a href="#" className="text-orange-600 text-sm font-medium hover:underline" onClick={e => { e.preventDefault(); setIsViewAllOpen(true); }}>
            {/* View All → */}
          </a>
          {planTourState.plantTourId && (
            <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-blue-800 text-xs font-mono">
              {/* <strong>Plan Tour ID:</strong> {planTourState.plantTourId} */}
            </div>
          )}
        </div>
        {/* Modal and Progress Bar logic ... */}
        {/* Modal */}
        <PlantTourModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onDone={handleStartPlantTour} isLoading={isPlantTourLoading} departmentName={getDepartmentName()} />
        {/* Bakery Noida Modal */}
        <BakeryNoidaModal isOpen={isBakeryNoidaModalOpen} onClose={() => setIsBakeryNoidaModalOpen(false)} onDone={handleBakeryNoidaPlantTour} isLoading={isPlantTourLoading} />
        {/* Progress Bar */}
        {isOfflineLoading && (
          <div className="fixed bottom-4 left-4 right-4 sm:left-1/3 sm:right-1/3 z-50 bg-white dark:bg-gray-800 border shadow-lg rounded-md px-4 py-2">
            <p className="text-sm mb-1">Syncing data...</p>
            <div className="w-full bg-gray-200 dark:bg-gray-700 h-2 rounded-full overflow-hidden">
              <div
                className="bg-blue-600 h-2 transition-all duration-300"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>
        )}
        {/* View All Modal */}
        {isViewAllOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg p-6 max-w-2xl w-full relative">
              <button className="absolute top-2 right-2 text-gray-500 hover:text-gray-800 dark:hover:text-gray-200" onClick={() => setIsViewAllOpen(false)}>
                ✕
              </button>
              <h2 className="text-lg font-semibold mb-4">All Redux Data</h2>
              <div className="overflow-auto max-h-[60vh] text-xs">
                <strong>User State:</strong>
                <pre className="bg-gray-100 dark:bg-gray-800 rounded p-2 mb-2">{JSON.stringify(userState, null, 2)}</pre>
                <strong>Plan Tour State:</strong>
                <pre className="bg-gray-100 dark:bg-gray-800 rounded p-2">{JSON.stringify(planTourState, null, 2)}</pre>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
